/* Implements Warm Academic theme styling for official exam simulation */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from './AuthBridge';
import { PageLoader } from './PageLoader';
import { checkAndTriggerFirstQuizOfDayToast } from './ToastNotification';
import { AdvanceSuggestionPanel } from './AdvanceSuggestionPanel';
import { ExamSession, QuestionDifficulty } from '../types';
import { 
  Loader2, MapPin, CheckCircle, ArrowRight, 
  ChevronLeft, ChevronRight,
  Award, Zap, CheckCircle2,
  AlertCircle, XCircle, BookOpen, Stethoscope, Sparkles,
  Globe, LayoutDashboard,
  Brain, X, RotateCcw, Save, 
  Clock, FastForward, Target, 
  Activity, ShieldCheck, ListOrdered, Undo2,
  Menu,
  ChevronDown,
  Microscope,
  ArrowUpRight,
  TrendingDown,
  Database,
  GraduationCap,
  Smile,
  Frown,
  Meh,
  SearchCheck,
  Binary,
  Info,
  Lock,
  Unlock,
  UserPlus,
  History,
  Search,
  Keyboard,
  Volume2,
  VolumeX,
  Printer,
  Download
} from 'lucide-react';
import { PrintExamDocument } from './PrintExamDocument';
import { downloadPDFFromElement } from '../services/pdfService';
import { 
  PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import { US_STATES, getMockSession } from '../data/mockData';
import { GamificationService } from '../services/gamificationService';
import { AdIntervalBreak } from './AdIntervalBreak';
import { SponsoredService } from '../services/sponsoredService';
import { GuestTrialService } from '../services/guestTrialService';
import { useThemeTokens } from '../hooks/useActiveTheme';

import { StorageService } from '../services/storageService';
import { MissedQuestionsService } from '../services/missedQuestionsService';
import { AdUnit } from './AdUnit';
import { DifficultyRatingWidget } from './DifficultyRatingWidget';
import { DifficultyRatingBadge } from './DifficultyRatingBadge';
import confetti from 'canvas-confetti';
import { getDeepRationale } from '../services/geminiService';
import { AiFeatureAuthModal } from './AiFeatureAuthModal';

interface ExamSimulatorProps {
  initialMode?: string;
  sessionOverride?: ExamSession;
}

interface CircularProgressRingProps {
  progress: number; // 0 to 100
  size?: number; // width/height in px
  strokeWidth?: number;
  accentColorClass?: string;
  trackColorClass?: string;
  icon?: React.ReactNode;
  label?: string;
  sublabel?: React.ReactNode;
  isWarning?: boolean;
}

const CircularProgressRing: React.FC<CircularProgressRingProps> = ({
  progress,
  size = 36,
  strokeWidth = 3.5,
  accentColorClass = 'text-indigo-600 dark:text-indigo-400',
  trackColorClass = 'text-slate-200 dark:text-slate-700',
  icon,
  label,
  sublabel,
  isWarning = false,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.min(100, Math.max(0, progress));
  const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;

  const colorClass = isWarning ? 'text-rose-500 animate-pulse' : accentColorClass;

  return (
    <div className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2.5 py-1 sm:py-1.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm shrink-0 min-w-0 max-w-full overflow-hidden">
      <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className={`fill-none stroke-current ${trackColorClass}`}
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className={`fill-none stroke-current ${colorClass} transition-all duration-500 ease-out`}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        {icon ? (
          <div className="absolute inset-0 flex items-center justify-center">
            {icon}
          </div>
        ) : (
          <span className={`absolute text-[7.5px] sm:text-[9px] font-black tracking-tighter tabular-nums ${isWarning ? 'text-rose-600 dark:text-rose-400' : 'text-slate-800 dark:text-slate-200'}`}>
            {Math.round(clampedProgress)}%
          </span>
        )}
      </div>
      {(label || sublabel) && (
        <div className="flex flex-col text-left pr-0.5 min-w-0 shrink-0">
          {label && (
            <span className={`text-[9.5px] sm:text-xs font-black tracking-tight tabular-nums whitespace-nowrap ${isWarning ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-slate-100'}`}>
              {label}
            </span>
          )}
          {sublabel && (
            <span className="text-[7px] sm:text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider leading-tight mt-0.5 whitespace-nowrap">
              {sublabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const ExamSimulator: React.FC<ExamSimulatorProps> = ({ initialMode = 'standard', sessionOverride }) => {
  const { user, activeCert, updateUser } = useAuth();
  const isGuest = !user;
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const tokens = useThemeTokens();

  const effectiveGuestExamLimit = (config?.guestExamLimit !== undefined)
      ? config.guestExamLimit
      : (activeCert?.guestExamLimit !== undefined ? activeCert.guestExamLimit : 1);

  const [guestExamsCompleted, setGuestExamsCompleted] = useState(() => GuestTrialService.getExamsCompletedCount());

  useEffect(() => {
    const handleUpdate = () => {
      setConfig(SponsoredService.getConfig());
    };
    const handleGuestUpdate = () => {
      setGuestExamsCompleted(GuestTrialService.getExamsCompletedCount());
    };
    window.addEventListener('sponsored-config-updated', handleUpdate);
    window.addEventListener('guest-trial-updated', handleGuestUpdate);
    return () => {
      window.removeEventListener('sponsored-config-updated', handleUpdate);
      window.removeEventListener('guest-trial-updated', handleGuestUpdate);
    };
  }, []);
  const { certId } = useParams<{ certId: string }>(); 
  const navigate = useNavigate();

  const activeTenant = certId || activeCert?.id || 'cna';

  const [stateCode, setStateCode] = useState<string>(user?.state || 'Global');
  const [examMode, setExamMode] = useState<string>(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [session, setSession] = useState<ExamSession | null>(sessionOverride || null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, string>>({}); 
  const [confidenceMap, setConfidenceMap] = useState<Record<string, 'high' | 'low'>>({});
  const [timeSpent, setTimeSpent] = useState<Record<string, number>>({}); 
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showMap, setStatusMap] = useState(false);

  const handleDownloadExamPdf = async () => {
    setIsGeneratingPdf(true);
    setTimeout(async () => {
      try {
        const titleSlug = session?.examConfig?.vendor || 'Practice_Exam';
        const cleanSlug = titleSlug.replace(/[^a-zA-Z0-9]/g, '_');
        await downloadPDFFromElement('print-exam-document', {
          fileName: `CNA_${cleanSlug}_Report.pdf`
        });
      } catch (err) {
        console.error('Failed to generate exam PDF:', err);
      } finally {
        setIsGeneratingPdf(false);
      }
    }, 120);
  };
  const [showAdBreak, setShowAdBreak] = useState(false);
  const [lastAdIndex, setLastAdIndex] = useState(-1);
  const [adRefreshKey, setAdRefreshKey] = useState(0);
  
  // Roadmap States
  const [roadmap, setRoadmap] = useState<any>(null);
  const [isRoadmapLoading, setIsRoadmapLoading] = useState(false);
  const [roadmapFilter, setRoadmapFilter] = useState<'all' | 'completed' | 'unattempted'>('all');
  const [roadmapSearch, setRoadmapSearch] = useState('');

  // Deep Rationale State
  const [deepRationale, setDeepRationale] = useState<any | null>(null);
  const [isDeepDiving, setIsDeepDiving] = useState(false);
  const [showAiAuthModal, setShowAiAuthModal] = useState(false);
  const [showGuestExamAuthModal, setShowGuestExamAuthModal] = useState(false);
  const [lockedSetTarget, setLockedSetTarget] = useState<number | null>(null);

  const [currentSequence, setCurrentSequence] = useState<string[]>([]);
  const [availableConfigs, setAvailableConfigs] = useState<any[]>([]);

  const isQuizMode = examMode === 'scenario_quiz' || examMode === 'practice';

  useEffect(() => {
    if (sessionOverride) {
      setSession(sessionOverride);
      setTimeRemaining(isQuizMode ? 0 : sessionOverride.examConfig.timeLimitMinutes * 60);
      if (window.trackEvent) window.trackEvent('simulation_launched', 'simulation', sessionOverride.examConfig.vendor);
    }
  }, [sessionOverride, isQuizMode]);

  // Auto-resume if query param ?resume=true is present
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const shouldResume = searchParams.get('resume') === 'true';
    if (shouldResume && !session && user?.currentSession) {
      const s = user.currentSession;
      if (s.session && Array.isArray(s.session.questions) && s.session.questions.length > 0) {
        setSession(s.session);
        setUserAnswers(s.userAnswers || {});
        setConfidenceMap(s.confidenceMap || {});
        setCurrentIndex(s.currentIndex || 0);
        setTimeRemaining(s.timeRemaining !== undefined ? s.timeRemaining : (s.session.examConfig?.timeLimitMinutes || 60) * 60);
        setTimeSpent(s.timeSpent || {});
        setExamMode(s.examMode || 'standard');
        setIsSubmitted(false);
        setIsReviewing(false);
        if (window.trackEvent) window.trackEvent('simulation_resumed', 'simulation', s.session.examConfig?.vendor || 'practice_exam');
      }
    }
  }, [user?.currentSession, session]);

  // Auto-sync session state to user.currentSession during active exam
  useEffect(() => {
    if (!session || isSubmitted || isReviewing || !user?.id) return;
    const sessionData = {
      certId: activeTenant,
      session,
      userAnswers,
      confidenceMap,
      currentIndex,
      timeRemaining,
      timeSpent,
      examMode,
      lastSaved: new Date().toISOString()
    };
    try {
      localStorage.setItem(`active_exam_session_${user.id}`, JSON.stringify(sessionData));
    } catch (e) {
      // ignore
    }
    const timer = setTimeout(() => {
      updateUser({ currentSession: sessionData }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [session, userAnswers, confidenceMap, currentIndex, timeRemaining, examMode, isSubmitted, isReviewing, user?.id, activeTenant]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const res = await fetch(`/practice-exams/api/exam-configs?certId=${activeTenant}`);
        const data = await res.json();
        setAvailableConfigs(data.items || []);
      } catch (e) {
        console.warn("Registry Preview Sync Failed.");
      }
    };
    fetchConfigs();
  }, [activeTenant]);

  useEffect(() => {
    const fetchRoadmap = async () => {
        if (session || !config.isFixedTestsEnabled) return;
        setIsRoadmapLoading(true);
        try {
            const data = await StorageService.getFixedTestRoadmap(activeTenant, stateCode);
            setRoadmap(data);
        } catch (e) {
            console.warn("Roadmap engine deferred.");
        } finally {
            setIsRoadmapLoading(false);
        }
    };
    fetchRoadmap();
  }, [activeTenant, stateCode, session, config.isFixedTestsEnabled]);

  const activePreviewConfig = useMemo(() => {
    const match = availableConfigs.find(c => c.stateCode === stateCode.toUpperCase());
    if (match) return match;
    const global = availableConfigs.find(c => c.stateCode === 'GLOBAL');
    return global || { timeLimitMinutes: 120, passingPercent: 75 };
  }, [availableConfigs, stateCode]);

  useEffect(() => {
    if (!session || isSubmitted || isReviewing || isQuizMode || showAdBreak) return;
    const interval = setInterval(() => {
        setTimeRemaining(prev => {
            if (prev <= 1) {
                finishExam();
                return 0;
            }
            return prev - 1;
        });
        const qId = session.questions[currentIndex]._id;
        setTimeSpent(prev => ({ ...prev, [qId]: (prev[qId] || 0) + 1 }));
    }, 1000);
    return () => clearInterval(interval);
  }, [session, currentIndex, isSubmitted, isReviewing, examMode, showAdBreak]);

  const activeAnalytics = useMemo(() => {
    if (!session) return null;
    const questions = session.questions;
    const totalQuestions = questions.length;
    const initialStats = { correctCount: 0, incorrectCount: 0, unansweredCount: 0, missedQuestionIds: [] as string[] };
    const categoryMap: Record<string, { correct: number, total: number, totalTime: number }> = {};
    
    const baseStats = questions.reduce((acc, q) => {
        const categoryName = q.category || 'General';
        if (!categoryMap[categoryName]) categoryMap[categoryName] = { correct: 0, total: 0, totalTime: 0 };
        categoryMap[categoryName].total++;
        categoryMap[categoryName].totalTime += (timeSpent[q._id] || 0);

        const answer = userAnswers[q._id];
        if (answer === undefined || answer === null || answer === '') { 
            acc.unansweredCount++; 
            acc.missedQuestionIds.push(q._id); 
        } else {
            if (q.type === 'ordering') {
                const correctSequence = q.options.map((o, oIdx) => o._id || oIdx.toString()).join(',');
                if (String(answer) === String(correctSequence)) {
                    acc.correctCount++;
                    categoryMap[categoryName].correct++;
                } else {
                    acc.incorrectCount++;
                    acc.missedQuestionIds.push(q._id);
                }
            } else {
                const correctOptionIdx = q.options.findIndex(o => !!o.isCorrect);
                const correctOption = q.options[correctOptionIdx];
                const correctId = correctOption ? (correctOption._id || correctOptionIdx.toString()) : 'no_correct_option_defined';
                
                if (String(answer) === String(correctId)) {
                    acc.correctCount++;
                    categoryMap[categoryName].correct++;
                } else { 
                    acc.incorrectCount++; 
                    acc.missedQuestionIds.push(q._id); 
                }
            }
        }
        return acc;
    }, initialStats);

    const accuracyValue = totalQuestions > 0 ? (baseStats.correctCount / totalQuestions) * 100 : 0;
    const totalTimeSpent = (Object.values(timeSpent) as number[]).reduce((a, b) => a + b, 0);
    const avgTime = totalQuestions > 0 ? Math.round(totalTimeSpent / totalQuestions) : 0;
    const totalXP = (baseStats.correctCount * 10) + (accuracyValue >= session.examConfig.passingPercent ? 200 : 50);

    return { 
        ...baseStats,
        totalQuestions,
        score: Math.round(accuracyValue),
        passed: accuracyValue >= session.examConfig.passingPercent,
        stateCode: session.examConfig.state,
        totalTimeSeconds: totalTimeSpent,
        totalTimeFormatted: `${Math.floor(totalTimeSpent / 60)}m ${totalTimeSpent % 60}s`,
        avgTimePerQuestion: avgTime,
        xpEarned: totalXP,
        categoryBreakdown: Object.entries(categoryMap).map(([name, stats]) => ({
            name,
            score: Math.round((stats.correct / stats.total) * 100),
            total: stats.total,
            correct: stats.correct
        })).sort((a, b) => a.score - b.score) 
    };
  }, [session, userAnswers, timeSpent]);

  const startExam = async (selectedState: string = stateCode, fixedIndex: number | null = null) => {
    // If guest user and all exams are locked (limit === 0)
    if (isGuest && effectiveGuestExamLimit <= 0) {
        setLockedSetTarget(fixedIndex !== null ? fixedIndex + 1 : 1);
        setShowGuestExamAuthModal(true);
        return;
    }

    // If guest user has completed allowed number of free practice exams
    if (isGuest && guestExamsCompleted >= effectiveGuestExamLimit) {
        setLockedSetTarget(fixedIndex !== null ? fixedIndex + 1 : null);
        setShowGuestExamAuthModal(true);
        return;
    }

    // For guest users, if a specific set is picked and it exceeds limit, prompt registration modal
    if (isGuest && fixedIndex !== null && fixedIndex >= effectiveGuestExamLimit) {
        setLockedSetTarget(fixedIndex + 1);
        setShowGuestExamAuthModal(true);
        return;
    }

    setIsLoading(true);
    try {
        if (user && selectedState !== user?.state) {
            await updateUser({ state: selectedState, stateManuallySet: true });
        }
        
        // For guest users, default to the free Set 0 if no set was explicitly picked
        const targetIndex = isGuest && fixedIndex === null ? 0 : fixedIndex;

        let data: ExamSession;
        try {
            data = await StorageService.startRemoteExam(activeTenant, selectedState, false, targetIndex);
        } catch (e) {
            if (activeTenant === 'cna') {
                data = getMockSession(activeTenant, selectedState, {});
            } else {
                throw new Error(`The logical registry for ${activeCert?.shortName || activeTenant} is currently empty. Provision items via the Administrative Factory.`);
            }
        }

        if (!data || !data.questions || data.questions.length === 0) {
            throw new Error(`The logical registry for ${activeCert?.shortName || activeTenant} is currently empty.`);
        }

        setSession(data);
        setTimeRemaining(isQuizMode ? 0 : data.examConfig.timeLimitMinutes * 60);
        setCurrentIndex(0);
        setUserAnswers({});
        setConfidenceMap({});
        setTimeSpent({});
        setCurrentSequence([]);
        setIsSubmitted(false);
        setIsReviewing(false);
        setLastAdIndex(-1);
        setShowAdBreak(false);
        setDeepRationale(null);
        if (user) {
            await updateUser({ currentSession: null });
        }

        if (window.trackEvent) window.trackEvent('simulation_launched', 'simulation', data.examConfig.vendor);
    } catch (e: any) {
        alert(e.message || "Exam bridge failed.");
    } finally {
        setIsLoading(false);
    }
  };

  const handleResume = () => {
    if (!user?.currentSession) return;
    const s = user.currentSession;
    if (s.certId !== activeTenant) {
        alert("Resumption protocol mismatch: The saved session belongs to a different certification.");
        return;
    }
    setSession(s.session);
    setUserAnswers(s.userAnswers || {});
    setConfidenceMap(s.confidenceMap || {});
    setCurrentIndex(s.currentIndex || 0);
    setTimeRemaining(s.timeRemaining || 0);
    setTimeSpent(s.timeSpent || {});
    setExamMode(s.examMode || 'standard');
    setIsSubmitted(false);
    setIsReviewing(false);

    if (window.trackEvent) window.trackEvent('simulation_resumed', 'simulation', s.session.examConfig.vendor);
  };

  const handleSaveAndExit = async () => {
    if (!session || !user?.id) return;
    setIsSaving(true);
    try {
        const sessionData = {
            certId: activeTenant,
            session,
            userAnswers,
            confidenceMap,
            currentIndex,
            timeRemaining,
            timeSpent,
            examMode,
            lastSaved: new Date().toISOString()
        };
        await updateUser({ currentSession: sessionData });
        if (window.trackEvent) window.trackEvent('simulation_saved', 'simulation', session.examConfig.vendor);
        navigate(`/${activeTenant}/dashboard`);
    } catch (e) {
        alert("Registry sync error: Could not persist session data.");
    } finally {
        setIsSaving(false);
    }
  };

  const finishExam = async () => {
    if (!session || !activeAnalytics) return;
    setIsSubmitted(true);
    checkAndTriggerFirstQuizOfDayToast();
    activeAnalytics.missedQuestionIds.forEach(qId => { MissedQuestionsService.addQuestion(qId); });
    
    // Log individual check results for exam questions
    try {
        session.questions.forEach((q: any, idx: number) => {
            const optionId = userAnswers[idx] || userAnswers[q._id];
            const optionObj = q.options?.find((o: any, oIdx: number) => o._id === optionId || oIdx.toString() === optionId);
            const isCorrect = optionObj?.isCorrect === true || 
                              String(optionObj?.isCorrect).toLowerCase() === 'true' || 
                              Number(optionObj?.isCorrect) === 1;

            StorageService.trackUserEvent({
                action: 'question_answer',
                category: 'exam_simulator',
                label: q.text || q.question || 'Exam Question',
                metadata: {
                    questionId: q._id,
                    wasCorrect: isCorrect,
                    optionId: optionId,
                    isExam: true,
                    topic: q.category || q.topic || 'General',
                    isGuest
                }
            });
        });

        // Log overall exam attempt
        StorageService.trackUserEvent({
            action: 'exam_attempt',
            category: 'exam_simulator',
            label: session.examConfig.title || session.examConfig.vendor || 'Clinical Exam Simulation',
            value: activeAnalytics.score,
            metadata: {
                totalQuestions: session.questions.length,
                score: activeAnalytics.score,
                passed: activeAnalytics.passed,
                isScenarioQuiz: examMode === 'scenario_quiz',
                isGuest
            }
        });
    } catch (e) {
        console.warn("Exam completions telemetry tracker skipped:", e);
    }

    if (user?.id) {
        const updatedStats = GamificationService.addXP(user.stats, activeAnalytics.xpEarned);
        const current = StorageService.getProgress(user.id);
        const newEntry = { 
            ...activeAnalytics, 
            id: `exam_${Date.now()}`, 
            date: new Date().toLocaleDateString(), 
            certId: activeTenant,
            examMode: examMode === 'scenario_quiz' ? 'Scenario Quiz' : 'Simulation',
            confidenceMap, 
            questions: session.questions,
            userAnswers,
            fixedTestId: session.fixedTestId
        };
        current.history = [newEntry, ...(current.history || [])].slice(0, 100);
        try {
            localStorage.removeItem(`active_exam_session_${user.id}`);
        } catch (e) {
            // ignore
        }
        await updateUser({ history: current.history, stats: updatedStats, currentSession: null });
    } else {
        try {
            const guestEntry = {
                score: activeAnalytics.score,
                passed: activeAnalytics.passed,
                date: new Date().toISOString(),
                stateCode,
                totalQuestions: session.questions.length,
                correctCount: activeAnalytics.correctCount,
                fixedTestId: session.fixedTestId || `${activeTenant}_${stateCode}_test_0`
            };
            localStorage.setItem('cna_guest_last_exam_result', JSON.stringify(guestEntry));
            GuestTrialService.trackExamCompleted(guestEntry.fixedTestId, effectiveGuestExamLimit);
            setGuestExamsCompleted(GuestTrialService.getExamsCompletedCount());
        } catch (e) {
            // ignore
        }
    }

    if (window.trackEvent) window.trackEvent('simulation_finished', 'simulation', session.examConfig.vendor, activeAnalytics.score);

    // Confetti celebration animation for score above 85%
    if (activeAnalytics.score >= 85) {
      try {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        setTimeout(() => {
          confetti({
            particleCount: 60,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.7 }
          });
          confetti({
            particleCount: 60,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.7 }
          });
        }, 250);
      } catch (err) {
        console.error("Confetti launch error:", err);
      }
    }
  };

  const handleNext = () => {
    if (!session) return;
    
    const nextIndex = currentIndex + 1;
    const intervalKey = examMode === 'scenario_quiz' ? 'scenarioQuiz' : (isQuizMode ? 'practiceQuiz' : 'practiceExam');
    const interval = config.intervals[intervalKey as keyof typeof config.intervals] || 10;
    const rotationThreshold = config.intervals.adRotationThreshold || 10;

    // Trigger ad rotation/refresh
    if (nextIndex > 0 && nextIndex % rotationThreshold === 0) {
        setAdRefreshKey(prev => prev + 1);
    }

    if (user?.planTier === 'free' && config.isActive && config.intermissionEnabled && nextIndex > 0 && nextIndex % interval === 0 && lastAdIndex !== nextIndex && nextIndex < session.questions.length) {
        setShowAdBreak(true);
        setLastAdIndex(nextIndex);
        return;
    }

    if (currentIndex === session.questions.length - 1) {
        if (isReviewing) { setIsReviewing(false); setIsSubmitted(true); }
        else { finishExam(); }
    } else {
        setCurrentIndex(nextIndex);
        setCurrentSequence([]);
        setDeepRationale(null);
        window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setCurrentSequence([]);
      setDeepRationale(null);
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const handleJump = (index: number) => {
    setCurrentIndex(index);
    setCurrentSequence([]);
    setStatusMap(false);
    setDeepRationale(null);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleAnswer = (questionId: string, optionId: string) => {
    if (isReviewing || isSubmitted || (isQuizMode && userAnswers[questionId])) return;
    const q = session?.questions[currentIndex];
    if (q?.type === 'ordering') {
        if (currentSequence.includes(optionId)) return;
        const next = [...currentSequence, optionId];
        setCurrentSequence(next);
        if (next.length === q.options.length) {
            setUserAnswers((prev: Record<string, string>) => ({ ...prev, [questionId]: next.join(',') }));
            if (!confidenceMap[questionId]) setConfidenceMap((prev: Record<string, 'high' | 'low'>) => ({...prev, [questionId]: 'high' as const}));
        }
    } else {
        setUserAnswers((prev: Record<string, string>) => ({ ...prev, [questionId]: optionId }));
        if (!confidenceMap[questionId]) setConfidenceMap((prev: Record<string, 'high' | 'low'>) => ({...prev, [questionId]: 'high' as const}));
    }
  };

  const setConfidence = (val: 'high' | 'low') => {
      const qId = session?.questions[currentIndex]?._id;
      if (qId) setConfidenceMap((prev: Record<string, 'high' | 'low'>) => ({ ...prev, [qId]: val }));
  };

  const handleDeepDive = async () => {
      if (!user) {
          setShowAiAuthModal(true);
          StorageService.trackUserEvent({
            action: 'exam_ai_rationale_guest_clicked',
            category: 'conversion',
            label: 'Guest clicked Exam AI Details',
            metadata: {
              vendor: session?.examConfig?.vendor,
              questionId: session?.questions[currentIndex]?._id
            }
          });
          return;
      }

      const currentQ = session?.questions[currentIndex];
      if (!currentQ || isDeepDiving) return;

      setIsDeepDiving(true);
      try {
          const correctOpt = currentQ.options.find(o => o.isCorrect);
          const data = await getDeepRationale(
              currentQ.text, 
              currentQ.options, 
              correctOpt?._id || '', 
              activeCert?.name || 'Nursing'
          );
          setDeepRationale(data);
          GamificationService.addPoints(15);
      } catch (e) {
          alert("Distractor Analysis Bridge Interrupt.");
      } finally {
          setIsDeepDiving(false);
      }
  };

  // Audio Speech Synthesis (TTS) State and Helper Hooks
  const [isSpeaking, setIsSpeaking] = useState(false);
  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      activeUtteranceRef.current = null;
    }
    setIsSpeaking(false);
  }, [currentIndex, isSubmitted]);

  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
      }
    };
  }, []);

  const handleSpeak = (textToSpeak: string) => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
        setIsSpeaking(false);
        return;
      }
      window.speechSynthesis.cancel();
      const cleanText = textToSpeak
        .replace(/<[^>]*>/g, ' ')
        .replace(/[*#`_~]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const utterance = new SpeechSynthesisUtterance(cleanText);
      activeUtteranceRef.current = utterance;
      utterance.onend = () => {
        activeUtteranceRef.current = null;
        setIsSpeaking(false);
      };
      utterance.onerror = () => {
        activeUtteranceRef.current = null;
        setIsSpeaking(false);
      };
      setIsSpeaking(true);
      window.speechSynthesis.speak(utterance);
    }
  };

  // Safe Keyboard Shortcuts similar to standard features
  useEffect(() => {
    const handleKeyShortcut = (e: KeyboardEvent) => {
        if (!session || isSaving || showMap || showAdBreak || isSubmitted) return;
        
        // Block shortcuts inside inputs
        const targetTag = e.target && (e.target as HTMLElement).tagName.toLowerCase();
        if (targetTag === 'input' || targetTag === 'textarea' || targetTag === 'select') return;

        const currentQ = session.questions[currentIndex];
        if (!currentQ) return;

        const userAns = userAnswers[currentQ._id];
        // If question can be advanced
        const canAdvance = isReviewing || (isQuizMode ? !!userAns : (userAns !== undefined && (currentQ.type === 'ordering' ? currentSequence.length === currentQ.options.length : true)));

        if (canAdvance) {
            if (e.code === 'Space' || e.code === 'ArrowRight' || e.code === 'Enter') {
                e.preventDefault();
                handleNext();
            }
        }
        
        if (!isReviewing && !(isQuizMode && userAns)) {
            // Options picker
            let indexToSelect = -1;
            if (e.code === 'KeyA' || e.code === 'Digit1') indexToSelect = 0;
            else if (e.code === 'KeyB' || e.code === 'Digit2') indexToSelect = 1;
            else if (e.code === 'KeyC' || e.code === 'Digit3') indexToSelect = 2;
            else if (e.code === 'KeyD' || e.code === 'Digit4') indexToSelect = 3;

            if (indexToSelect !== -1) {
                const opt = currentQ.options[indexToSelect];
                if (opt) {
                    const val = opt._id || indexToSelect.toString();
                    if (currentQ.type !== 'ordering' || !currentSequence.includes(val)) {
                        e.preventDefault();
                        handleAnswer(currentQ._id, val);
                    }
                }
            }
        }
    };

    window.addEventListener('keydown', handleKeyShortcut);
    return () => window.removeEventListener('keydown', handleKeyShortcut);
  }, [session, currentIndex, userAnswers, currentSequence, isReviewing, isQuizMode, isSaving, showMap, showAdBreak, isSubmitted]);

  if (isLoading) return <PageLoader message="Loading Practice Test..." text="Initializing Simulation Engine" />;

  if (showAdBreak) {
      return <AdIntervalBreak moduleName={examMode === 'scenario_quiz' ? "Scenario Quiz" : "Exam Simulation"} onComplete={() => setShowAdBreak(false)} />;
  }

  if (isSubmitted && activeAnalytics) {
    const correctCount = activeAnalytics.correctCount ?? 0;
    const incorrectCount = activeAnalytics.incorrectCount ?? 0;
    const unansweredCount = activeAnalytics.unansweredCount ?? 0;
    const totalCount = activeAnalytics.totalQuestions ?? 1;
    const passingPercent = session?.examConfig?.passingPercent || 75;
    const scoreDiff = activeAnalytics.score - passingPercent;

    const confidenceStats = (() => {
        let mastered = 0, lucky = 0, blindspot = 0, gap = 0;
        session?.questions?.forEach(q => {
            const answer = userAnswers[q._id];
            const hasAnswered = answer !== undefined && answer !== null && answer !== '';
            const isHigh = !confidenceMap || confidenceMap[q._id] === 'high' || confidenceMap[q._id] === undefined;

            let isCorrect = false;
            if (hasAnswered) {
                if (q.type === 'ordering') {
                    const reqSeq = q.options.map((o, oIdx) => o._id || oIdx.toString()).join(',');
                    isCorrect = String(answer) === String(reqSeq);
                } else {
                    const cIdx = q.options.findIndex(o => !!o.isCorrect);
                    const cOption = q.options[cIdx];
                    const cId = cOption ? (cOption._id || cIdx.toString()) : 'no_correct';
                    isCorrect = String(answer) === String(cId);
                }
            }
            if (isCorrect) { if (isHigh) mastered++; else lucky++; }
            else { if (isHigh) blindspot++; else gap++; }
        });
        return { mastered, lucky, blindspot, gap };
    })();

    // Determine pacing profile
    const pacingProfile = (() => {
        const avg = activeAnalytics.avgTimePerQuestion;
        if (avg < 20) return { name: "Very Fast", desc: "You are finishing very fast! Double check your work to avoid making silly mistakes.", color: "text-amber-500 bg-amber-500/10 border-amber-500/20" };
        if (avg <= 50) return { name: "Good Speed", desc: "Great timing! You have a very good pacing.", color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20" };
        return { name: "Slow & Careful", desc: "You are being careful, but try to watch the total timer on the real test to make sure you finish.", color: "text-indigo-500 bg-indigo-500/10 border-indigo-500/20" };
    })();

    return (
      <>
        <div className="max-w-6xl mx-auto py-6 md:py-10 px-3 space-y-8 md:space-y-12 animate-fade-in text-slate-800 dark:text-slate-100 no-print">
            {/* HERO SEGMENT: DYNAMIC LOGIC REPORT CARD */}
            <div className={`rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-14 shadow-xl relative overflow-hidden ${tokens.heroBannerBg} border-b-[8px] md:border-b-[12px] transition-all duration-1000 ${activeAnalytics.passed ? 'border-emerald-500 shadow-emerald-950/20' : 'border-rose-500 shadow-rose-950/20'}`}>
                <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-indigo-500/10 blur-[130px] pointer-events-none rounded-full translate-x-1/3 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-emerald-500/5 blur-[100px] pointer-events-none rounded-full -translate-x-1/3 translate-y-1/3"></div>
                
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                    <div className="lg:col-span-8 space-y-6 md:space-y-8 w-full">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                            <div className={`p-4 md:p-5 rounded-3xl shadow-xl ring-8 ring-white/5 shrink-0 ${activeAnalytics.passed ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white' : 'bg-gradient-to-br from-rose-400 to-rose-600 text-white'}`}>
                                {activeAnalytics.passed ? <Award size={36} className="animate-pulse" /> : <AlertCircle size={36} />}
                            </div>
                            <div>
                                <span className={`inline-block px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.2em] mb-2 ${activeAnalytics.passed ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'}`}>
                                    {activeAnalytics.passed ? 'Test Passed!' : 'Practice and Study More'}
                                </span>
                                <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-none text-white">
                                    {activeAnalytics.passed ? 'Test Passed!' : 'Need More Practice'}
                                </h1>
                                <p className="text-slate-400 text-[10px] md:text-xs font-bold tracking-[0.15em] ml-0.5 mt-2.5 flex items-center gap-2 uppercase">
                                    <Globe size={13} className="text-indigo-400" /> {activeAnalytics.stateCode} State Practice Test
                                </p>
                            </div>
                        </div>

                        {/* PASSING THRESHOLD OFFSET SLIDER */}
                        <div className="bg-white/5 border border-white/10 rounded-2.5xl p-5 md:p-6 space-y-3.5 backdrop-blur-md">
                            <div className="flex justify-between items-center text-xs">
                                <span className="font-bold text-slate-400 flex items-center gap-1.5"><Target size={14} className="text-indigo-400" /> My Score vs Passing Target</span>
                                <span className={`font-black ${scoreDiff >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {scoreDiff >= 0 ? `You scored +${scoreDiff}% above target!` : `You need ${Math.abs(scoreDiff)}% more to pass`}
                                </span>
                            </div>
                            <div className="relative pt-2 pb-1">
                                <div className="h-2 w-full bg-slate-800 rounded-full relative overflow-hidden">
                                    {/* Passing indicator line */}
                                    <div className="absolute top-0 bottom-0 left-[75%] w-0.5 bg-sky-400 z-20"></div>
                                    <div 
                                        className={`h-full rounded-full transition-all duration-1000 ${activeAnalytics.passed ? 'bg-gradient-to-r from-indigo-500 to-emerald-500' : 'bg-gradient-to-r from-indigo-500 to-rose-500'}`}
                                        style={{ width: `${Math.min(activeAnalytics.score, 100)}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between text-[8px] font-black text-slate-500 mt-2 tracking-widest uppercase">
                                    <span>0% Base</span>
                                    <span className="text-sky-400 translate-x-2">75% Passing Score</span>
                                    <span>100% Perfect</span>
                                </div>
                            </div>
                        </div>

                        {/* STATS QUICK GRID */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 pt-2">
                            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-4">
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Your Score</p>
                                <p className="text-2xl md:text-3xl font-black text-white tabular-nums">{activeAnalytics.score}%</p>
                            </div>
                            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-4">
                                <p className="text-xs font-bold text-slate-505 uppercase tracking-wider mb-1">Time Taken</p>
                                <p className="text-2xl md:text-3xl font-black text-white tabular-nums">{activeAnalytics.totalTimeFormatted}</p>
                            </div>
                            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-4">
                                <p className="text-xs font-bold text-slate-505 uppercase tracking-wider mb-1">Time per Question</p>
                                <p className="text-2xl md:text-3xl font-black text-indigo-300 tabular-nums">{activeAnalytics.avgTimePerQuestion}s <span className="text-[10px] font-black text-slate-500 uppercase">/ question</span></p>
                            </div>
                            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-4">
                                <p className="text-xs font-bold text-slate-505 uppercase tracking-wider mb-1">Points Earned</p>
                                <p className="text-2xl md:text-3xl font-black text-emerald-400 tabular-nums">+{activeAnalytics.xpEarned} <span className="text-[10px] font-black text-slate-505 uppercase">Points</span></p>
                            </div>
                        </div>
                    </div>

                    {/* INTERACTIVE ROUNDED GAUGE CHART */}
                    <div className="lg:col-span-4 flex flex-col items-center justify-center bg-slate-900/40 border border-white/5 rounded-[2rem] p-6 lg:p-8 w-full max-w-sm mx-auto">
                        <div className="shrink-0 relative w-44 h-44 md:w-52 md:h-52 flex items-center justify-center">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie 
                                        data={[{ value: activeAnalytics.score }, { value: Math.max(0, 100 - activeAnalytics.score) }]} 
                                        cx="50%" cy="50%" 
                                        innerRadius={68} 
                                        outerRadius={88} 
                                        startAngle={225} 
                                        endAngle={-45} 
                                        dataKey="value" 
                                        stroke="none" 
                                        cornerRadius={8}
                                    >
                                        <Cell fill={activeAnalytics.passed ? '#10b981' : '#f43f5e'} />
                                        <Cell fill="rgba(255,255,255,0.06)" />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                                <span className="text-4xl md:text-5xl font-black tracking-tighter text-white">{activeAnalytics.score}%</span>
                                <span className={`text-[8.5px] font-black tracking-widest uppercase mt-1 ${activeAnalytics.passed ? 'text-emerald-400' : 'text-rose-455'}`}>
                                    {activeAnalytics.passed ? 'PASSED' : 'NOT PASSED'}
                                </span>
                            </div>
                        </div>
                        <div className="text-center mt-3 pt-3 border-t border-white/5 w-full">
                            <p className="text-[9px] font-black text-slate-505 tracking-widest uppercase">Your Result Summary</p>
                            <p className="text-[11px] font-bold text-slate-300 mt-1 leading-relaxed">
                                {activeAnalytics.passed 
                                    ? 'Good job! You passed based on your state\'s test rules.'
                                    : 'Keep studying! Double check topics where you got questions wrong.'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* GUEST BANNER ON EXAM COMPLETION */}
            {isGuest && (
                <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 text-white rounded-[2.5rem] p-6 md:p-8 shadow-xl relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6 animate-slide-up border border-emerald-400/30">
                    <div className="space-y-2 max-w-xl relative z-10">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-[9px] font-black tracking-widest uppercase text-emerald-100">
                            <Sparkles size={12} /> Free Trial Completed
                        </div>
                        <h3 className="text-xl md:text-2xl font-black tracking-tight leading-tight">
                            Unlock All Exam Sets & Save Your Results
                        </h3>
                        <p className="text-xs md:text-sm text-emerald-50/90 font-medium leading-relaxed">
                            Congratulations on completing your free practice exam set! Create a 100% free student account to unlock all {roadmap?.numTests ? `${roadmap.numTests} full exam sets` : 'exam sets'}, permanently save your test history, build your Mistake Vault, and access faculty clinical rationales.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 w-full md:w-auto relative z-10">
                        <button
                            onClick={() => {
                                const currentUrl = window.location.href;
                                window.location.href = `https://www.diamondnursingschool.com/studyhub-login?mode=register&redirect=${encodeURIComponent(currentUrl)}`;
                            }}
                            className="py-3.5 px-6 bg-white text-emerald-800 hover:bg-emerald-50 rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                        >
                            <UserPlus size={16} /> Create Free Account
                        </button>
                        <button
                            onClick={() => {
                                const currentUrl = window.location.href;
                                window.location.href = `https://www.diamondnursingschool.com/studyhub-login?redirect=${encodeURIComponent(currentUrl)}`;
                            }}
                            className="py-3.5 px-5 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 flex items-center justify-center gap-1.5"
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            )}

            {/* POST-EXAM DIFFICULTY RATING INPUT & COMMUNITY AVERAGE */}
            <DifficultyRatingWidget 
                itemId={`exam-${session?.examConfig?.vendor || examMode || 'standard'}`}
                itemTitle={`${session?.examConfig?.vendor || 'Practice Exam'} Simulation`}
                isExam={true}
                userId={user?.id || 'guest'}
            />

            {/* COGNITIVE CALIBRATION: NEW CONFIDENCE & ACCURACY BENTO GRID */}
            <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                    <h3 className={`text-sm font-black tracking-[0.3em] uppercase ${tokens.accentText} flex items-center gap-2`}>
                        <Brain size={16} className="text-indigo-500" /> Your Study Analysis
                    </h3>
                    <p className="text-xs text-slate-400 dark:text-slate-500 font-bold">Check whether you were sure of your answers or just guessing to find where you need to study.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                        {
                            title: "Correct (Sure of it)",
                            count: confidenceStats.mastered,
                            desc: "You were very sure of these answers and got them right! Great job.",
                            theme: "bg-emerald-500/5 dark:bg-emerald-500/[0.03] border-emerald-500/20 text-emerald-600 dark:text-emerald-400",
                            valColor: "text-emerald-500",
                            icon: <CheckCircle2 size={16} />
                        },
                        {
                            title: "Correct (Unsure / Guessed)",
                            count: confidenceStats.lucky,
                            desc: "You got these right, but marked that you were unsure. Practice these more to be sure!",
                            theme: "bg-amber-500/5 dark:bg-amber-500/[0.03] border-amber-500/20 text-amber-600 dark:text-amber-400",
                            valColor: "text-amber-500",
                            icon: <Activity size={16} />
                        },
                        {
                            title: "Wrong (Sure of it)",
                            count: confidenceStats.blindspot,
                            desc: "You thought these were correct but missed them. Read these explanations carefully.",
                            theme: "bg-rose-500/5 dark:bg-rose-500/[0.03] border-rose-500/20 text-rose-600 dark:text-rose-400",
                            valColor: "text-rose-500",
                            icon: <XCircle size={16} />
                        },
                        {
                            title: "Wrong (Unsure)",
                            count: confidenceStats.gap,
                            desc: "You knew you weren't sure and got these wrong. Good starting point to study more.",
                            theme: "bg-slate-500/5 dark:bg-slate-500/[0.03] border-slate-500/20 text-slate-605 dark:text-slate-400",
                            valColor: "text-slate-550",
                            icon: <Info size={16} />
                        }
                    ].map((card, idx) => (
                        <div key={idx} className={`p-6 border rounded-[2rem] flex flex-col justify-between transition-all hover:-translate-y-1 ${card.theme}`}>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    {card.icon}
                                    <h4 className="text-xs font-black tracking-tight">{card.title}</h4>
                                </div>
                                <p className="text-[10px] leading-relaxed opacity-80">{card.desc}</p>
                            </div>
                            <div className="mt-4 pt-4 border-t border-current/10 flex items-baseline justify-between">
                                <span className="text-[8.5px] font-black uppercase opacity-65">Number of Questions</span>
                                <span className={`text-3xl font-black ${card.valColor}`}>{card.count}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* INTERACTIVE DIAGNOSTIC CHECKS BENTO-GRID */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {/* CARD 1: DIAGNOSTIC SPLIT CHECKLIST */}
                <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4 flex flex-col justify-between group">
                    <div>
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className="p-1.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg"><SearchCheck size={16}/></div>
                            <h3 className="text-xs font-black text-slate-450 tracking-wider uppercase">Answers</h3>
                        </div>
                        <h4 className="text-base font-black text-slate-900 dark:text-white">Your Answer Summary</h4>
                        <p className="text-[10px] font-medium text-slate-400 leading-relaxed mt-1">A simple breakdown of how you did on the questions.</p>
                    </div>

                    <div className="space-y-3.5 my-4">
                        <div className="flex justify-between items-center py-1 border-b border-slate-50 dark:border-slate-800/40">
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500"/> Correct Answers</span>
                            <span className="text-xs font-black text-slate-900 dark:text-white tabular-nums">+{correctCount} / {totalCount}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-slate-50 dark:border-slate-800/40">
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 flex items-center gap-2"><XCircle size={14} className="text-rose-500"/> Wrong Answers</span>
                            <span className="text-xs font-black text-slate-900 dark:text-white tabular-nums">{incorrectCount} Wrong</span>
                        </div>
                        <div className="flex justify-between items-center py-1">
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 flex items-center gap-2"><Info size={14} className="text-slate-400"/> Skipped Questions</span>
                            <span className="text-xs font-black text-slate-905 dark:text-white tabular-nums">{unansweredCount} Left</span>
                        </div>

                        {/* SPLIT MULTI-COLOR SEGMENT BAR */}
                        <div className="flex h-3.5 w-full rounded-2xl overflow-hidden mt-6 bg-slate-100 dark:bg-slate-955 border border-slate-200/50 dark:border-slate-800 p-0.5">
                            <div className="bg-emerald-500 h-full rounded-l-md transition-all duration-1000" style={{ width: `${(correctCount / totalCount) * 100}%` }}></div>
                            <div className="bg-rose-500 h-full transition-all duration-1000" style={{ width: `${(incorrectCount / totalCount) * 100}%` }}></div>
                            <div className="bg-slate-350 dark:bg-slate-700 h-full rounded-r-md transition-all duration-1000" style={{ width: `${(unansweredCount / totalCount) * 100}%` }}></div>
                        </div>
                        <div className="flex justify-between text-[7.5px] font-black text-slate-400 dark:text-slate-500 tracking-widest uppercase px-1">
                            <span>Correct ({Math.round((correctCount/totalCount)*100)}%)</span>
                            <span>Incorrect ({Math.round(((incorrectCount+unansweredCount)/totalCount)*100)}%)</span>
                        </div>
                    </div>
                </div>

                {/* CARD 2: PASE & DECISION VELOCITY EVALUATION */}
                <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4 flex flex-col justify-between group">
                    <div>
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className="p-1.5 bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-455 rounded-lg"><Clock size={16}/></div>
                            <h3 className="text-xs font-black text-slate-450 tracking-wider uppercase">Time Used</h3>
                        </div>
                        <h4 className="text-base font-black text-slate-900 dark:text-white">Timer Details</h4>
                        <p className="text-[10px] font-medium text-slate-400 leading-relaxed mt-1">See how fast or slow you are answering questions.</p>
                    </div>

                    <div className="my-auto space-y-4 py-3">
                        <div className={`p-4 rounded-2xl border text-xs leading-relaxed space-y-1 ${pacingProfile.color}`}>
                            <p className="font-black uppercase tracking-wider text-[10px]">Speed: {pacingProfile.name}</p>
                            <p className="opacity-90">{pacingProfile.desc}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800/40 rounded-xl">
                                <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">My Speed</span>
                                <span className="text-sm font-black text-slate-900 dark:text-white tabular-nums">{activeAnalytics.avgTimePerQuestion}s</span>
                                <span className="text-[8px] text-slate-400 font-bold ml-1">avg/question</span>
                            </div>
                            <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800/40 rounded-xl">
                                <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Time Limit</span>
                                <span className="text-sm font-black text-indigo-600 dark:text-indigo-400">120s max</span>
                                <span className="text-[8px] text-slate-400 font-bold ml-1">per question</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CARD 3: REWARDS & REPETITION GAIN */}
                <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4 flex flex-col justify-between group">
                    <div>
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className="p-1.5 bg-amber-50 dark:bg-amber-950/40 text-amber-505 dark:text-amber-400 rounded-lg"><Zap size={16}/></div>
                            <h3 className="text-xs font-black text-slate-450 tracking-wider uppercase">Points & Streak</h3>
                        </div>
                        <h4 className="text-base font-black text-slate-900 dark:text-white">Points Earned</h4>
                        <p className="text-[10px] font-medium text-slate-400 leading-relaxed mt-1">Points you earned for finishing this practice test.</p>
                    </div>

                    <div className="space-y-3.5 my-4">
                        <div className="flex justify-between items-center py-2 bg-gradient-to-r from-emerald-500/10 to-transparent p-3 rounded-xl border border-emerald-500/10">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-emerald-505 text-white rounded-lg"><Sparkles size={11} /></div>
                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Base Points</span>
                            </div>
                            <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 tabular-nums">+{activeAnalytics.score * 10} Points</span>
                        </div>
                        <div className="flex justify-between items-center py-2 bg-gradient-to-r from-indigo-500/10 to-transparent p-3 rounded-xl border border-indigo-500/10">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-indigo-505 text-white rounded-lg"><Award size={11} /></div>
                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Passing Bonus</span>
                            </div>
                            <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 tabular-nums">
                                {activeAnalytics.passed ? '+200 Points' : '+50 Points'}
                            </span>
                        </div>
                        <div className="text-[9.5px] font-bold text-slate-400 dark:text-slate-505 text-center uppercase tracking-wider py-1.5">
                            "Practice on a daily routine to unlock the ultimate study rank!"
                        </div>
                    </div>
                </div>
            </div>

            {/* DOMAIN EVALUATION & PROFICIENCY MATRIX */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
                {/* LEFT CONSOLE: DETAILED DOMAIN BREAKDOWN */}
                <div className="lg:col-span-8 bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 md:p-10 border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">
                    <div>
                        <h3 className="text-lg md:text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                            <Microscope size={22} className="text-indigo-600" /> How you did by Topic
                        </h3>
                        <p className="text-[10px] md:text-xs font-bold text-slate-400 tracking-widest uppercase mt-1">Check which topics you are best at and where you need more study</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                        {activeAnalytics.categoryBreakdown.map((domain, i) => {
                            // Categorize the scoring quality and provide dynamic styling & advice tips
                            const domainStatus = (() => {
                                const s = domain.score;
                                if (s >= 80) return { 
                                    label: "EXCELLENT", 
                                    color: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900/50",
                                    bar: "bg-emerald-505",
                                    tip: "Awesome work! You know this topic very well." 
                                };
                                if (s >= 70) return { 
                                    label: "GOOD", 
                                    color: "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 border-indigo-100 dark:border-indigo-900/50",
                                    bar: "bg-indigo-505",
                                    tip: "Nice baseline. Study a bit more to get a perfect score." 
                                };
                                if (s >= 50) return { 
                                    label: "NEEDS STUDY", 
                                    color: "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border-amber-100 dark:border-amber-900/50",
                                    bar: "bg-amber-505",
                                    tip: "You missed a few questions here. Read lessons and study key words carefully." 
                                };
                                return { 
                                    label: "NEEDS STUDY", 
                                    color: "text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border-rose-100 dark:border-rose-900/50", 
                                    bar: "bg-rose-505",
                                    tip: "You missed many questions on this topic. Please read the lessons and practice more."
                                };
                            })();

                            return (
                                <div key={i} className="space-y-3 p-4 bg-slate-50/50 dark:bg-slate-950/30 rounded-2xl border border-slate-100 dark:border-slate-800/60 hover:shadow-md transition-all group duration-350">
                                    <div className="flex justify-between items-start gap-4">
                                        <div className="min-w-0">
                                            <p className="text-[11px] md:text-xs font-black text-slate-800 dark:text-white tracking-tight truncate">{domain.name}</p>
                                            <p className="text-[8.5px] font-bold text-slate-405 tracking-wider uppercase mt-1">
                                                {domain.correct} / {domain.total} Questions Right
                                            </p>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-black tracking-widest ${domainStatus.color}`}>
                                            {domainStatus.label}
                                        </span>
                                    </div>

                                    {/* DYNAMIC PROGRESS BAR */}
                                    <div className="space-y-1">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[8px] font-bold text-slate-400 uppercase">Proficiency</span>
                                            <span className="text-xs font-black text-slate-700 dark:text-slate-300 tabular-nums">{domain.score}%</span>
                                        </div>
                                        <div className="h-2 w-full bg-slate-100 dark:bg-slate-950 rounded-full overflow-hidden border border-slate-200/40 dark:border-slate-800 shadow-inner">
                                            <div 
                                                className={`h-full rounded-full transition-all duration-1200 ease-out group-hover:brightness-110 ${domainStatus.bar}`} 
                                                style={{ width: `${domain.score}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                    <p className="text-[9px] font-medium text-slate-455 dark:text-slate-400 italic leading-snug pt-1 border-t border-slate-50 dark:border-slate-800/30">
                                        "{domainStatus.tip}"
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT CONSOLE: ACTIONS & ADVICE CARD */}
                <div className="lg:col-span-4 space-y-6 md:space-y-8">
                    {/* READINESS INDEX EXPLAINER */}
                    <div className={`rounded-[2.5rem] p-6 md:p-8 text-white relative overflow-hidden shadow-xl border border-white/10 ${tokens.heroBannerBg}`}>
                        <div className="absolute top-0 right-0 p-6 opacity-[0.03] pointer-events-none">
                            <Brain size={140} />
                        </div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className={`p-2 ${tokens.primaryBtn} rounded-2xl shadow-xl`}>
                                <Sparkles size={18} className="text-white" />
                            </div>
                            <h4 className={`text-xs font-black tracking-[0.2em] ${tokens.accentText} uppercase`}>How You Did</h4>
                        </div>
                        
                        <p className="text-xs md:text-sm font-bold text-slate-200 leading-relaxed opacity-95">
                            {activeAnalytics.passed 
                                ? `Excellent performance! You passed based on your state's test rules. We recommend reviewing any questions you missed to try for a perfect score next time.` 
                                : `Keep practicing! Your score was below the passing mark of 75%. We recommend looking over the questions you missed and practicing with flashcards and flash quizzes.`}
                        </p>
                    </div>

                    {/* ENGAGING ACTION TOOLBAR */}
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 md:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
                        <h4 className="text-[10px] font-black text-slate-404 dark:text-slate-400 tracking-widest flex items-center gap-2 uppercase">
                            <Target size={16} className={tokens.accentText} /> What to do next
                        </h4>
                        
                        <div className="space-y-3">
                            {isGuest && (
                                <button 
                                    onClick={() => {
                                        const currentUrl = window.location.href;
                                        window.location.href = `https://www.diamondnursingschool.com/studyhub-login?mode=register&redirect=${encodeURIComponent(currentUrl)}`;
                                    }}
                                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-600 to-teal-600 text-white rounded-2xl transition-all group shadow-md hover:brightness-110 active:scale-95"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/20 text-white rounded-xl group-hover:scale-110 transition-transform"><Sparkles size={16}/></div>
                                        <div className="text-left">
                                            <p className="text-xs font-black text-white">Unlock All Exam Sets</p>
                                            <p className="text-[8px] text-white/80 font-bold uppercase mt-0.5">Sign up free to access all {roadmap?.numTests ? `${roadmap.numTests} full exam sets` : 'exam sets'}</p>
                                        </div>
                                    </div>
                                    <ArrowRight size={16} className="text-white/80 group-hover:text-white group-hover:translate-x-1 transition-all" />
                                </button>
                            )}

                            <button 
                                onClick={() => { setIsReviewing(true); setIsSubmitted(false); setCurrentIndex(0); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                className={`w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/50 dark:bg-slate-955 dark:hover:bg-slate-800/80 border border-transparent ${tokens.cardHoverBorder} rounded-2xl transition-all group shadow-sm hover:shadow`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl group-hover:scale-110 transition-transform"><BookOpen size={16}/></div>
                                    <div className="text-left">
                                        <p className="text-xs font-black text-slate-800 dark:text-white">Review All Answers</p>
                                        <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5">See which ones you got right or wrong</p>
                                    </div>
                                </div>
                                <ArrowUpRight size={16} className="text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
                            </button>

                            <button 
                                onClick={handleDownloadExamPdf}
                                disabled={isGeneratingPdf}
                                className={`w-full flex items-center justify-between p-4 bg-emerald-50/60 hover:bg-emerald-100/60 dark:bg-emerald-950/30 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 ${tokens.cardHoverBorder} rounded-2xl transition-all group shadow-sm hover:shadow disabled:opacity-50`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-emerald-600 text-white rounded-xl group-hover:scale-110 transition-transform">
                                        {isGeneratingPdf ? <Loader2 size={16} className="animate-spin" /> : <Download size={16}/>}
                                    </div>
                                    <div className="text-left">
                                        <p className="text-xs font-black text-slate-800 dark:text-white">
                                            {isGeneratingPdf ? 'Generating PDF File...' : 'Download PDF Report'}
                                        </p>
                                        <p className="text-[8px] text-slate-500 dark:text-slate-400 font-bold uppercase mt-0.5">Export PDF directly with questions & rationales</p>
                                    </div>
                                </div>
                                <Download size={16} className="text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                            </button>

                            <button 
                                onClick={() => window.print()}
                                className={`w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/50 dark:bg-slate-955 dark:hover:bg-slate-800/80 border border-transparent ${tokens.cardHoverBorder} rounded-2xl transition-all group shadow-sm hover:shadow`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl group-hover:scale-110 transition-transform"><Printer size={16}/></div>
                                    <div className="text-left">
                                        <p className="text-xs font-black text-slate-800 dark:text-white">Print via Browser</p>
                                        <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5">Open browser print dialog</p>
                                    </div>
                                </div>
                                <Printer size={16} className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
                            </button>
                            
                            <button 
                                onClick={() => { setSession(null); setIsSubmitted(false); setIsReviewing(false); setUserAnswers({}); setConfidenceMap({}); setTimeSpent({}); }}
                                className={`w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/50 dark:bg-slate-955 dark:hover:bg-slate-800/80 border border-transparent ${tokens.cardHoverBorder} rounded-2xl transition-all group shadow-sm hover:shadow`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-pink-50 dark:bg-pink-950 text-pink-600 dark:text-pink-455 rounded-xl group-hover:scale-110 transition-transform"><RotateCcw size={16}/></div>
                                    <div className="text-left">
                                        <p className="text-xs font-black text-slate-800 dark:text-white">Take Another Test</p>
                                        <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5">Start again with a different state or set of questions</p>
                                    </div>
                                </div>
                                <ArrowRight size={16} className="text-slate-400 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors" />
                            </button>

                            <button 
                                onClick={() => navigate(`/${activeTenant}/dashboard`)}
                                className={`w-full flex items-center justify-between p-4 ${tokens.primaryBtn} border border-transparent rounded-2xl transition-all group shadow-md`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/10 text-white rounded-xl group-hover:scale-110 transition-transform"><LayoutDashboard size={16}/></div>
                                    <div className="text-left">
                                        <p className="text-xs font-black text-white">Go to Dashboard</p>
                                        <p className="text-[8px] opacity-80 font-bold uppercase mt-0.5">Go back to the home dashboard screen</p>
                                    </div>
                                </div>
                                <ArrowRight size={16} className="text-white/40 group-hover:text-white transition-colors" />
                            </button>
                        </div>
                    </div>

                    <AdUnit placement="results" />
                </div>
            </div>

            {/* Next Best Action Advisor */}
            <div className="bg-white dark:bg-slate-950 p-6 md:p-10 rounded-[2.5rem] border border-slate-150 dark:border-slate-850 shadow-xl mt-8">
                <AdvanceSuggestionPanel 
                    source="exam" 
                    score={activeAnalytics.score} 
                    moduleTitle={session?.examConfig?.vendor || "Simulation Practice Exam"} 
                />
            </div>
        </div>
        <PrintExamDocument 
          examTitle={session?.examConfig?.vendor ? `${session.examConfig.vendor} Practice Exam` : "National Board Practice Exam"}
          certName={activeCert?.name || "Nurse Aide Certification (CNA)"}
          vendor={session?.examConfig?.vendor || "Registry Curated"}
          questions={session?.questions || []}
          userAnswers={userAnswers}
          isSubmitted={true}
          score={activeAnalytics.percentScore}
          passed={activeAnalytics.passed}
          timeSpentMinutes={Math.round((Object.values(timeSpent).reduce((a, b) => a + b, 0) || 0) / 60)}
          categoryBreakdown={activeAnalytics.categoryBreakdown}
        />
        <AiFeatureAuthModal 
          isOpen={showGuestExamAuthModal}
          onClose={() => setShowGuestExamAuthModal(false)}
          featureTitle={lockedSetTarget ? `Unlock Practice Exam Set ${lockedSetTarget.toString().padStart(2, '0')}` : "Unlock All Practice Exam Sets"}
          featureDescription="Create your free student account or sign in to unlock all full-length state-aligned practice exam sets, save your exam scores, and build your Mistake Vault."
          source={`exam_simulator_locked_set_${lockedSetTarget || 'all'}`}
          badgeLabel="Free Student Account Required"
          benefits={[
            "Access all practice exam sets across every state and board vendor",
            "Save all test attempts, detailed question breakdowns, and score trajectories",
            "Auto-collect missed questions into your personal Mistake Vault",
            "100% Free forever with no credit card required"
          ]}
          dismissText="Close"
        />
        <AiFeatureAuthModal 
          isOpen={showAiAuthModal}
          onClose={() => setShowAiAuthModal(false)}
          featureTitle="Faculty Clinical Exam Rationale"
          featureDescription="Create a free account or sign in to access instant educator-curated distractor analyses and board-aligned clinical rationales."
          source="exam_simulator_deep_dive"
        />
      </>
    );
  }

  if (!session) {
      const hasSavedSession = user?.currentSession && (user.currentSession.certId === activeTenant || !user.currentSession.certId);
      
      const highestScore = (() => {
        if (!user?.history || user.history.length === 0) return 0;
        const scores = user.history.map(h => h.score || 0);
        return scores.length > 0 ? Math.max(...scores) : 0;
      })();

      const passedTestsCount = (() => {
          if (!roadmap) return 0;
          let count = 0;
          for (let idx = 0; idx < roadmap.numTests; idx++) {
              const testId = `${activeTenant.toLowerCase()}_${stateCode.toLowerCase()}_test_${idx}`;
              const attempt = user?.history?.find(h => h.fixedTestId === testId);
              if (attempt?.passed) {
                  count++;
              }
          }
          return count;
      })();
      const roadmapMastery = roadmap && roadmap.numTests > 0 ? Math.round((passedTestsCount / roadmap.numTests) * 100) : 0;

      const getThematicTestName = (idx: number) => {
        return `Comprehensive Examination Set ${idx + 1}`;
      };

      const getThematicTestDesc = (idx: number) => {
        const descs = [
          "Master foundational ethics, safety metrics, vital procedures, and resident health standards.",
          "Comprehensive coverage on sanitation, sterile boundaries, PPE, and contagion vectors.",
          "Practice physical care metrics, weight measurement, fluid monitoring, and clinical logs."
        ];
        if (idx < descs.length) return descs[idx];
        return "Assess multi-domain competence with comprehensive board questions.";
      };

      const filteredRoadmapIndices = (() => {
        if (!roadmap) return [];
        return Array.from({ length: roadmap.numTests }).map((_, idx) => idx).filter(idx => {
           const testId = `${activeTenant.toLowerCase()}_${stateCode.toLowerCase()}_test_${idx}`;
           const attempt = user?.history?.find(h => h.fixedTestId === testId);
           
           if (roadmapSearch) {
              const nameMatch = getThematicTestName(idx).toLowerCase().includes(roadmapSearch.toLowerCase());
              const descMatch = getThematicTestDesc(idx).toLowerCase().includes(roadmapSearch.toLowerCase());
              if (!nameMatch && !descMatch) return false;
           }

           if (roadmapFilter === 'completed') return !!attempt;
           if (roadmapFilter === 'unattempted') return !attempt;
           return true;
        });
      })();

      return (
        <>
        <div className="max-w-7xl mx-auto py-6 md:py-10 px-3 md:px-6 space-y-8 md:space-y-12 animate-fade-in text-slate-900 dark:text-slate-100 no-print">
            {/* BREADCRUMB NAVIGATION */}
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[#4A5568] dark:text-slate-400 bg-white dark:bg-slate-900 px-4 py-2.5 rounded-2xl border border-[#E3DFD5] dark:border-slate-800 shadow-xs">
              <span onClick={() => navigate('/dashboard')} className="hover:text-[#0D6E6E] cursor-pointer transition-colors font-bold text-slate-600 dark:text-slate-300">Dashboard</span>
              <ChevronRight size={13} className="text-slate-300" />
              <span className="font-bold text-[#0D6E6E] dark:text-teal-400">Practice Exam</span>
              <ChevronRight size={13} className="text-slate-300" />
              <span className="text-slate-700 dark:text-slate-200 font-bold">{stateCode} CNA</span>
              <ChevronRight size={13} className="text-slate-300" />
              <span className="text-slate-400 font-medium">Exam Set</span>
            </div>

            {/* HERO SEGMENT */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                {/* Simulated Board Arena & Options Left side */}
                <div className={`lg:col-span-8 p-6 md:p-10 rounded-[2.5rem] flex flex-col justify-between shadow-2xl relative overflow-hidden group isolate border ${
                  tokens.isWarmAcademic
                    ? 'bg-[#0D6E6E] text-white border-[#0B5C5C]'
                    : tokens.isClinical
                    ? 'bg-gradient-to-br from-[#0B2942] via-[#0D3C5C] to-[#071F33] border-teal-500/20 text-white'
                    : tokens.isHighContrast
                    ? 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                    : tokens.isMinimal
                    ? 'bg-slate-900 text-white border-slate-800'
                    : tokens.isVibrant
                    ? 'bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-950 border-violet-800 text-white'
                    : 'bg-gradient-to-br from-indigo-950 via-slate-950 to-slate-900/95 border-indigo-500/10 text-white'
                }`}>
                    <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                        <GraduationCap size={220} className="text-white transform rotate-6 animate-pulse" />
                    </div>
                    
                    <div className="relative z-10 space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="px-3 py-1 bg-white/10 rounded-full font-black text-[9px] tracking-widest uppercase text-teal-100">Online Practice Center</span>
                            <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full font-black text-[9px] tracking-widest uppercase flex items-center gap-1.5">
                                <Sparkles size={8} /> Passing Score: {activePreviewConfig.passingPercent}%
                            </span>
                        </div>
                        <h2 className={`text-3xl md:text-5xl font-black tracking-tight leading-none text-white ${tokens.isClinical ? 'font-serif' : ''}`}>
                            Exam Simulator
                        </h2>
                        <p className="text-xs md:text-sm text-slate-100 font-medium max-w-xl leading-relaxed">
                            Practice for your license. Choose a randomized test or pick an exam set below.
                        </p>
                    </div>

                    {/* SELECT STATE AND SESSION MODES */}
                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 pt-8 border-t border-white/10">
                        <div className="space-y-2">
                            <label className="text-[9px] font-black text-teal-100 tracking-wider ml-1 flex items-center gap-1.5">
                                <Globe size={11} /> SELECT STATE
                            </label>
                            <div className="relative group">
                                <select 
                                    value={stateCode} 
                                    onChange={e => setStateCode(e.target.value)} 
                                    className="w-full bg-white/10 border border-white/20 pl-4 pr-10 py-3 rounded-xl font-bold text-xs outline-none appearance-none cursor-pointer hover:bg-white/20 focus:ring-2 focus:ring-amber-400 transition-all text-white"
                                >
                                    {US_STATES.map(s => <option key={s.code} value={s.code} className="text-slate-900">{s.name}</option>)}
                                </select>
                                <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-white pointer-events-none" />
                            </div>
                        </div>

                        {/* MODE SELECT SEGMENT */}
                        <div className="space-y-2">
                            <label className="text-[9px] font-black text-teal-100 tracking-wider ml-1 flex items-center gap-1.5">
                                <Zap size={11} /> CHOOSE HOW TO STUDY
                            </label>
                            <div className="grid grid-cols-2 gap-1 bg-white/10 border border-white/20 p-1 rounded-xl">
                                <button 
                                    type="button"
                                    onClick={() => setExamMode('standard')}
                                    className={`py-2 px-3 text-[10px] font-black rounded-lg transition-all ${
                                      examMode === 'standard' 
                                        ? (tokens.isWarmAcademic
                                            ? 'bg-[#E05638] text-white shadow-md'
                                            : tokens.isClinical
                                            ? 'bg-[#0F7D6C] text-white shadow-md'
                                            : tokens.isHighContrast
                                            ? 'bg-amber-400 text-black border border-black font-black'
                                            : tokens.isMinimal
                                            ? 'bg-slate-800 text-white'
                                            : tokens.isVibrant
                                            ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                                            : 'bg-indigo-600 text-white shadow-md')
                                        : 'text-slate-200 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    Real Exam Mode (Timed)
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setExamMode('practice')}
                                    className={`py-2 px-3 text-[10px] font-black rounded-lg transition-all ${
                                      examMode === 'practice' 
                                        ? (tokens.isWarmAcademic
                                            ? 'bg-[#E05638] text-white shadow-md'
                                            : tokens.isClinical
                                            ? 'bg-[#0F7D6C] text-white shadow-md'
                                            : tokens.isHighContrast
                                            ? 'bg-amber-400 text-black border border-black font-black'
                                            : tokens.isMinimal
                                            ? 'bg-slate-800 text-white'
                                            : tokens.isVibrant
                                            ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                                            : 'bg-indigo-600 text-white shadow-md')
                                        : 'text-slate-200 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    Practice Mode (Instant Feedback)
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 pt-6 mt-6 flex flex-col md:flex-row items-stretch md:items-center gap-3">
                        <button 
                            onClick={() => startExam()} 
                            className={`flex-1 py-4 px-6 md:py-5 text-center font-black text-xs tracking-widest hover:brightness-110 md:sm text-white rounded-2xl active:scale-95 shadow-xl transition-all border-b-4 ${
                              tokens.isWarmAcademic
                                ? 'bg-[#E05638] border-[#C8472B]'
                                : tokens.isClinical
                                ? 'bg-[#0F7D6C] border-[#0B6356]'
                                : tokens.isHighContrast
                                ? 'bg-amber-400 border-b-4 border-black text-black font-black'
                                : tokens.isMinimal
                                ? 'bg-slate-800 border-slate-900'
                                : tokens.isVibrant
                                ? 'bg-gradient-to-r from-violet-600 to-indigo-600 border-indigo-800'
                                : (examMode === 'practice' ? 'bg-indigo-600 border-indigo-800' : 'bg-medical-500 border-medical-700')
                            } flex items-center justify-center gap-2`}
                        >
                            {hasSavedSession ? 'Start Fresh Practice Test' : 'Start Practice Test'} <FastForward size={14} />
                        </button>
                        
                        {hasSavedSession && (
                            <button 
                                onClick={handleResume}
                                className="py-4 px-6 md:py-5 border-2 border-white/30 text-white hover:bg-white/10 rounded-2xl active:scale-95 flex items-center justify-center gap-2 transition-all font-black text-[10px] md:text-xs tracking-widest shrink-0"
                            >
                                <RotateCcw size={14} /> Resume Saved Test
                            </button>
                        )}
                    </div>
                </div>

                {/* Right side stats insights cards */}
                <div className="lg:col-span-4 bg-white dark:bg-slate-900 border border-slate-205 dark:border-slate-855 p-6 rounded-[2.5rem] shadow-xl flex flex-col justify-between space-y-6">
                    <div className="space-y-6">
                        <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
                            <Activity size={16} className={tokens.accentText} />
                            <h3 className="text-xs font-black tracking-widest uppercase text-slate-400">My Practice Stats</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-850 rounded-2xl">
                                <p className="text-[9px] font-black tracking-wider text-slate-400 dark:text-slate-500 uppercase">POINTS</p>
                                <p className={`text-xl font-black mt-1.5 tabular-nums ${
                                  tokens.isClinical ? 'text-[#0F7D6C] dark:text-teal-400' : tokens.isHighContrast ? 'text-amber-500' : tokens.isVibrant ? 'text-violet-600 dark:text-violet-400' : 'text-indigo-600 dark:text-indigo-400'
                                }`}>+{user?.stats?.points || 0}</p>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-850 rounded-2xl">
                                <p className="text-[9px] font-black tracking-wider text-slate-400 dark:text-slate-500 uppercase">COMPLETE</p>
                                <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-1.5 tabular-nums">{(user?.history || []).length} Exams</p>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-850 rounded-2xl">
                                <p className="text-[9px] font-black tracking-wider text-slate-400 dark:text-slate-500 uppercase">TEST TIMER</p>
                                <p className="text-xl font-black text-slate-700 dark:text-slate-300 mt-1.5 tabular-nums">{examMode === 'scenario_quiz' ? '∞' : `${activePreviewConfig.timeLimitMinutes}m`}</p>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-850 rounded-2xl">
                                <p className="text-[9px] font-black tracking-wider text-slate-400 dark:text-slate-500 uppercase">PASS SCORE</p>
                                <p className="text-xl font-black text-slate-700 dark:text-slate-300 mt-1.5 tabular-nums">{activePreviewConfig.passingPercent}%</p>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-850 rounded-2xl col-span-2">
                                <p className="text-[9px] font-black tracking-wider text-slate-400 dark:text-slate-500 uppercase">HIGHEST SCORE</p>
                                <p className="text-xl font-black text-amber-500 dark:text-amber-400 mt-1.5 tabular-nums">
                                    {highestScore > 0 ? `${highestScore}% Acc` : 'No Attempts'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="p-4 bg-amber-500/10 rounded-2xl border border-amber-500/20 flex gap-3">
                            <Info size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5 animate-bounce-slow" />
                            <div className="space-y-1">
                                <p className="text-[10px] font-black text-amber-800 dark:text-amber-300 uppercase tracking-wider">Tutor Tip</p>
                                <p className="text-[9.5px] font-semibold text-slate-600 dark:text-slate-400 leading-relaxed">
                                    Use <span className="text-amber-600 dark:text-amber-300 font-bold">Practice mode</span> for studying with instant answers and clear explanations from our teachers! Changing modes will not affect your progress.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>            {/* STRUCTURED EXAMS SECTION */}
            {config.isFixedTestsEnabled && (
                <section className="space-y-6 md:space-y-8 animate-slide-up">
                    <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-6 border-b border-slate-150 dark:border-slate-800 pb-6">
                        <div className="space-y-1">
                            <h3 className={`text-xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3 ${tokens.isClinical ? 'font-serif' : ''}`}>
                                <ListOrdered className={tokens.accentText} /> Set Practice Exams
                            </h3>
                            <p className="text-xs font-bold text-slate-400 dark:text-slate-555 tracking-wider leading-relaxed">
                                A sequence of <span className={`font-extrabold ${tokens.accentText}`}>{roadmap ? roadmap.numTests : 0} detailed exam sets</span> covering all certified items. Complete sequentially to build logical board confidence.
                            </p>
                        </div>

                        {roadmap && (
                            <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl shadow-sm flex items-center justify-between gap-6 min-w-xs">
                                <div className="text-center pr-4 border-r border-slate-200 dark:border-slate-800 shrink-0">
                                    <p className="text-2xl font-black text-slate-900 dark:text-white tabular-nums">{roadmap.numTests}</p>
                                    <p className="text-[8px] font-black text-slate-400 dark:text-slate-500 tracking-widest uppercase">Exam Sets</p>
                                </div>
                                <div className="flex-1 space-y-1 shrink-0">
                                    <div className="flex justify-between text-[9px] font-black tracking-wider uppercase">
                                        <span className="text-slate-400">Sets Finished</span>
                                        <span className="text-indigo-600 dark:text-indigo-400">{roadmapMastery}%</span>
                                    </div>
                                    <div className="h-2 w-full bg-slate-200 dark:bg-slate-850 rounded-full overflow-hidden">
                                        <div className="h-full bg-indigo-500 transition-all duration-1000" style={{ width: `${roadmapMastery}%` }}></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* GUEST MODE NOTICE BANNER */}
                    {isGuest && (
                        <div className="bg-gradient-to-r from-amber-500/10 via-indigo-500/10 to-teal-500/10 border border-amber-300/40 dark:border-amber-700/40 p-4 md:p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-md shrink-0">
                                    <Sparkles size={18} />
                                </div>
                                <div>
                                    <p className="text-xs font-black text-slate-900 dark:text-white">
                                        {effectiveGuestExamLimit <= 0 
                                            ? 'Guest Mode • Sign-Up Required for Mock Exams' 
                                            : guestExamsCompleted >= effectiveGuestExamLimit
                                            ? `Guest Mode • Free Exam Limit Reached (${guestExamsCompleted}/${effectiveGuestExamLimit} Completed)`
                                            : effectiveGuestExamLimit === 1
                                            ? 'Guest Mode • 1 Free Practice Set Available'
                                            : `Guest Mode • ${effectiveGuestExamLimit - guestExamsCompleted} of ${effectiveGuestExamLimit} Free Practice Sets Remaining`}
                                    </p>
                                    <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 mt-0.5">
                                        {effectiveGuestExamLimit <= 0
                                            ? `Full state-standard mock exams require a free student account. Create your free account in 30 seconds to unlock all ${roadmap?.numTests || 0} exam suites and track your progress!`
                                            : guestExamsCompleted >= effectiveGuestExamLimit
                                            ? `You have completed your free guest practice exam quota (${guestExamsCompleted} of ${effectiveGuestExamLimit} full state tests taken). Create a free account to unlock all ${roadmap?.numTests || 0} exam sets and save your scores!`
                                            : effectiveGuestExamLimit === 1
                                            ? `You have full free access to Exam Set 01 (${roadmap?.questionCount || 70} questions based on ${stateCode} state standards). Create a free account to unlock all ${roadmap?.numTests || 0} exam sets and save your scores!`
                                            : `You have full free access to Exam Sets 01 through ${effectiveGuestExamLimit.toString().padStart(2, '0')} (${roadmap?.questionCount || 70} questions each based on ${stateCode} state standards). Create a free account to unlock all ${roadmap?.numTests || 0} exam sets and save your scores!`}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setLockedSetTarget(null);
                                    setShowGuestExamAuthModal(true);
                                }}
                                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 shrink-0 flex items-center gap-1.5"
                            >
                                <UserPlus size={14} /> Unlock All Sets Free
                            </button>
                        </div>
                    )}

                    {/* ROADMAP FILTER TABS & SEARCH */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-4">
                        {/* Tab Switcher */}
                        <div className="flex flex-wrap gap-1.5 bg-slate-100 dark:bg-slate-955 p-1 border border-slate-200 dark:border-slate-800 rounded-2xl w-fit">
                            <button 
                                onClick={() => setRoadmapFilter('all')}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all ${roadmapFilter === 'all' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-md' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-350'}`}
                            >
                                All Sets ({roadmap ? roadmap.numTests : 0})
                            </button>
                            <button 
                                onClick={() => setRoadmapFilter('completed')}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all ${roadmapFilter === 'completed' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-md' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-350'}`}
                            >
                                Completed ({user?.history?.filter(h => h.fixedTestId?.includes(activeTenant.toLowerCase())).length || 0})
                            </button>
                            <button 
                                onClick={() => setRoadmapFilter('unattempted')}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all ${roadmapFilter === 'unattempted' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-md' : 'text-slate-500 hover:text-slate-705'}`}
                            >
                                Unattempted ({roadmap ? Math.max(0, roadmap.numTests - (user?.history?.filter(h => h.fixedTestId?.includes(activeTenant.toLowerCase())).length || 0)) : 0})
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full md:w-64 max-w-md">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                            <input 
                                type="text"
                                value={roadmapSearch}
                                onChange={e => setRoadmapSearch(e.target.value)}
                                placeholder="Search fixed suites..."
                                className="w-full bg-slate-100 dark:bg-slate-955 border border-slate-205 dark:border-slate-800 pl-10 pr-4 py-2 rounded-2xl text-[11px] font-semibold text-slate-800 dark:text-white outline-none focus:bg-white dark:focus:bg-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all shadow-inner"
                            />
                            {roadmapSearch && (
                                <button onClick={() => setRoadmapSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-400 rounded-full"><X size={10} /></button>
                            )}
                        </div>
                    </div>

                    {isRoadmapLoading ? (
                        <PageLoader compact message="Loading exam roadmap indices..." />
                    ) : roadmap && roadmap.numTests > 0 ? (
                        filteredRoadmapIndices.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredRoadmapIndices.map((idx) => {
                                    const testId = `${activeTenant.toLowerCase()}_${stateCode.toLowerCase()}_test_${idx}`;
                                    const attempt = user?.history?.find(h => h.fixedTestId === testId);
                                    const isPassed = attempt?.passed;
                                    
                                    const examTitle = getThematicTestName(idx);
                                    const examDesc = getThematicTestDesc(idx);
                                    const isLockedForGuest = isGuest && (idx >= effectiveGuestExamLimit || guestExamsCompleted >= effectiveGuestExamLimit || effectiveGuestExamLimit <= 0);

                                    const handleCardClick = () => {
                                        if (isLockedForGuest) {
                                            setLockedSetTarget(idx + 1);
                                            setShowGuestExamAuthModal(true);
                                        } else {
                                            startExam(stateCode, idx);
                                        }
                                    };

                                    return (
                                        <div 
                                            key={idx}
                                            onClick={handleCardClick}
                                            className={`group p-6 rounded-[2.2rem] border-2 text-left transition-all relative overflow-hidden flex flex-col justify-between h-56 cursor-pointer transform hover:-translate-y-1 active:scale-[0.98] ${
                                                isLockedForGuest
                                                    ? 'bg-slate-50/80 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800/80 hover:border-indigo-400/60 dark:hover:border-indigo-500/40 hover:shadow-lg'
                                                    : attempt 
                                                        ? (isPassed 
                                                            ? (tokens.isWarmAcademic ? 'bg-[#E6F4F1] border-[#0D6E6E]/30 text-[#0D6E6E]' : 'bg-emerald-500/[0.03] dark:bg-emerald-500/[0.01] hover:bg-emerald-500/[0.05] border-emerald-500/20 shadow-emerald-500/[0.02] shadow-xl')
                                                            : (tokens.isWarmAcademic ? 'bg-orange-50 border-orange-200 text-orange-900' : 'bg-rose-500/[0.03] dark:bg-rose-500/[0.01] hover:bg-rose-500/[0.05] border-rose-500/20 shadow-rose-500/[0.02] shadow-xl')
                                                           ) 
                                                        : (tokens.isWarmAcademic
                                                            ? 'bg-white dark:bg-slate-900 border-[#E3DFD5] dark:border-slate-800 hover:border-[#0D6E6E] hover:shadow-md'
                                                            : config?.enableVibrantCards !== false
                                                            ? 'bg-gradient-to-br from-indigo-50/70 via-indigo-50/30 to-indigo-100/10 border-indigo-200/80 dark:from-slate-900/90 dark:via-indigo-950/35 dark:to-indigo-950/55 dark:border-indigo-800/60 shadow-[0_4px_20px_-4px_rgba(99,102,241,0.08)] hover:border-indigo-450 hover:shadow-2xl'
                                                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-indigo-400 hover:shadow-2xl hover:shadow-indigo-505/10'
                                                           )
                                            }`}
                                        >
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-start gap-2">
                                                    <span className={`px-2.5 py-0.5 rounded-full font-black text-[8px] tracking-widest uppercase flex items-center gap-1 ${
                                                        isLockedForGuest
                                                            ? 'bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-700'
                                                            : isGuest && idx < effectiveGuestExamLimit
                                                            ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                                                            : attempt 
                                                                ? (isPassed ? 'bg-[#E6F4F1] text-[#0D6E6E] border border-[#0D6E6E]/20' : 'bg-orange-100 text-orange-800 border border-orange-200')
                                                                : tokens.isWarmAcademic ? 'bg-[#E6F4F1] text-[#0D6E6E] border border-[#0D6E6E]/20' : 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-500 dark:text-indigo-400 border border-indigo-150/40 dark:border-indigo-900/30'
                                                    }`}>
                                                        {isLockedForGuest ? (
                                                            <>
                                                                <Lock size={9} /> Locked • Sign-Up Required
                                                            </>
                                                        ) : isGuest && idx < effectiveGuestExamLimit ? (
                                                            <>
                                                                <Sparkles size={9} /> Free Guest Set • Set {(idx + 1).toString().padStart(2, '0')}
                                                            </>
                                                        ) : attempt ? (
                                                            isPassed ? 'Completed ✓' : 'In Progress'
                                                        ) : (
                                                            `Not Started • Set ${(idx + 1).toString().padStart(2, '0')}`
                                                        )}
                                                    </span>
                                                    
                                                    {attempt && !isLockedForGuest && (
                                                        <span className={`text-xs font-black px-2 py-0.5 rounded-lg border tabular-nums ${isPassed ? 'bg-emerald-555 text-emerald-600 dark:text-emerald-400 border-emerald-500/10' : 'bg-rose-555 text-rose-500 dark:text-rose-400 border-rose-500/10'}`}>
                                                            {attempt.score}% Score
                                                        </span>
                                                    )}

                                                    {isLockedForGuest && (
                                                        <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-900/40">
                                                            Free Sign-Up
                                                        </span>
                                                    )}
                                                </div>

                                                <h4 className="text-base font-black text-slate-950 dark:text-slate-50 tracking-tight leading-tight pt-1.5 transition-colors group-hover:text-[#0D6E6E] dark:group-hover:text-teal-400">
                                                    {examTitle}
                                                </h4>
                                                
                                                <p className="text-[11px] text-slate-505 dark:text-slate-400 font-bold leading-normal pt-0.5 line-clamp-3">
                                                    {examDesc}
                                                </p>
                                                
                                                <div className="pt-2">
                                                    <DifficultyRatingBadge itemId={`exam-${testId}`} isExam={true} compact={true} />
                                                </div>
                                            </div>

                                            <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-850/40 flex justify-between items-center text-[9px] font-black text-slate-400">
                                                <span className="flex items-center gap-1">
                                                    <BookOpen size={12} className="text-[#0D6E6E] dark:text-teal-400 shrink-0" />
                                                    <span className="tabular-nums">{roadmap.questionCount} Questions</span>
                                                </span>
                                                <span className={`flex items-center gap-0.5 group-hover:translate-x-1.5 transition-transform font-black ${
                                                    isLockedForGuest 
                                                        ? 'text-indigo-600 dark:text-indigo-400' 
                                                        : 'text-[#E05638] dark:text-teal-400'
                                                }`}>
                                                    {isLockedForGuest ? (
                                                        <>Sign Up to Unlock <Lock size={10} className="ml-0.5"/></>
                                                    ) : isGuest && idx < effectiveGuestExamLimit ? (
                                                        <>Start Free Set {(idx + 1).toString().padStart(2, '0')} <ChevronRight size={10} /></>
                                                    ) : attempt ? (
                                                        <>Retake Set <ChevronRight size={10} /></>
                                                    ) : (
                                                        <>Start <ChevronRight size={10} /></>
                                                    )}
                                                </span>
                                            </div>

                                            {/* Corner badge icons */}
                                            <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full rotate-12 bg-slate-50 dark:bg-slate-850 flex items-end justify-start p-3 opacity-[0.03] group-hover:scale-110 group-hover:opacity-[0.1] transition-all">
                                                {isLockedForGuest ? <Lock size={28} /> : attempt ? (isPassed ? <ShieldCheck size={28} /> : <History size={28} />) : <Target size={28} />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="bg-slate-50 dark:bg-slate-950 py-16 text-center border border-dashed border-slate-200 dark:border-slate-855 rounded-3xl">
                                <Search className="mx-auto mb-4 text-slate-400" size={32} />
                                <p className="text-xs font-black text-slate-400 tracking-widest uppercase">No exam suites match your parameters</p>
                                <button onClick={() => { setRoadmapFilter('all'); setRoadmapSearch(''); }} className="mt-4 px-6 py-2 bg-indigo-50 text-indigo-600 rounded-xl font-black text-[9px] tracking-widest uppercase hover:bg-slate-100 transition-all">Reset Filters</button>
                            </div>
                        )
                    ) : (
                        <div className="bg-white rounded-[2.5rem] p-16 text-center border-2 border-dashed border-slate-100 opacity-60">
                            <Microscope size={48} className="mx-auto mb-4 text-slate-300" />
                            <p className="text-sm font-bold text-slate-500 tracking-widest">"Logical depth for {stateCode} is currently insufficient for partitioning."</p>
                        </div>
                    )}
                </section>
            )}

            <AdUnit placement="dashboard" />
        </div>
        <AiFeatureAuthModal 
          isOpen={showGuestExamAuthModal}
          onClose={() => setShowGuestExamAuthModal(false)}
          featureTitle={lockedSetTarget ? `Unlock Practice Exam Set ${lockedSetTarget.toString().padStart(2, '0')}` : "Unlock All Practice Exam Sets"}
          featureDescription="Create your free student account or sign in to unlock all full-length state-aligned practice exam sets, save your exam scores, and build your Mistake Vault."
          source={`exam_simulator_locked_set_${lockedSetTarget || 'all'}`}
          badgeLabel="Free Student Account Required"
          benefits={[
            "Access all practice exam sets across every state and board vendor",
            "Save all test attempts, detailed question breakdowns, and score trajectories",
            "Auto-collect missed questions into your personal Mistake Vault",
            "100% Free forever with no credit card required"
          ]}
          dismissText={effectiveGuestExamLimit > 0 ? (effectiveGuestExamLimit === 1 ? "Continue with Free Exam Set 01" : `Continue with Free Exam Sets (01-${effectiveGuestExamLimit.toString().padStart(2, '0')})`) : "Close"}
        />
        </>
      );
  }

  const currentQ = session.questions[currentIndex];
  const userAns = userAnswers[currentQ._id];
  const isOrdering = currentQ.type === 'ordering';
  const currentConfidence = confidenceMap[currentQ._id];

  const totalQuestions = session?.questions?.length || 1;
  const answeredCount = Object.keys(userAnswers).length;
  const remainingQuestions = Math.max(0, totalQuestions - answeredCount);
  const questionsProgressPercent = Math.min(100, Math.max(0, Math.round((answeredCount / totalQuestions) * 100)));

  const initialTimeSeconds = (session?.examConfig?.timeLimitMinutes || 60) * 60;
  const timeRemainingPercent = isQuizMode ? 100 : Math.min(100, Math.max(0, Math.round((timeRemaining / (initialTimeSeconds || 1)) * 100)));

  return (
    <>
      <div className="max-w-4xl mx-auto pb-8 md:pb-32 animate-fade-in text-slate-900 px-0 md:px-0 min-h-0 pt-2 md:pt-4 no-print">
      <div className="w-full">
        <AdUnit placement={isQuizMode ? 'practice_quiz_top' : 'practice_exam_top'} refreshKey={adRefreshKey} />
      </div>
      
      {showMap && (
          <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-md flex items-start md:items-start justify-center p-0 md:p-6 pt-10 md:pt-16 animate-fade-in overflow-y-auto custom-scrollbar">
              <div className="bg-white dark:bg-slate-900 rounded-t-[2.5rem] md:rounded-[3rem] shadow-2xl w-full max-w-4xl h-auto max-h-[90vh] flex flex-col animate-slide-up overflow-hidden border border-slate-200 dark:border-slate-800">
                  <div className="px-8 py-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
                      <div>
                          <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">Your Test Status</h3>
                          <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1 uppercase">Questions & Visual Real-Time Progress</p>
                      </div>
                      <button onClick={() => setStatusMap(false)} className="p-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-2xl transition-all active:scale-90 flex items-center justify-center min-h-[44px] min-w-[44px]"><X size={24}/></button>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-5 md:p-8 custom-scrollbar space-y-8">
                      {/* Detailed Circular Gauges Header in Status Drawer */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 bg-slate-50 dark:bg-slate-800/60 rounded-3xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm">
                          <div className="flex items-center gap-4">
                              <CircularProgressRing 
                                progress={questionsProgressPercent}
                                size={52}
                                strokeWidth={4.5}
                                accentColorClass={tokens.accentText}
                                label={`${questionsProgressPercent}%`}
                              />
                              <div>
                                  <h4 className="text-sm font-black text-slate-900 dark:text-slate-100">Questions Progress</h4>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                                    <span className="font-bold text-slate-800 dark:text-slate-200">{remainingQuestions} Remaining</span> ({answeredCount}/{totalQuestions} Answered)
                                  </p>
                              </div>
                          </div>

                          {!isQuizMode && (
                              <div className="flex items-center gap-4">
                                  <CircularProgressRing 
                                    progress={timeRemainingPercent}
                                    size={52}
                                    strokeWidth={4.5}
                                    accentColorClass={tokens.accentText}
                                    isWarning={timeRemaining < 300}
                                    label={`${timeRemainingPercent}%`}
                                  />
                                  <div>
                                      <h4 className="text-sm font-black text-slate-900 dark:text-slate-100">Time Remaining</h4>
                                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium font-mono tabular-nums mt-0.5">
                                        {Math.floor(timeRemaining / 60)}m {(timeRemaining % 60).toString().padStart(2, '0')}s left of {session?.examConfig?.timeLimitMinutes || 60}m
                                      </p>
                                  </div>
                              </div>
                          )}
                      </div>

                      <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2 md:gap-3">
                          {(session?.questions || []).map((q, idx) => {
                              const answered = !!userAnswers[q._id];
                              const active = idx === currentIndex;
                              return (
                                  <button 
                                    key={q._id} 
                                    onClick={() => handleJump(idx)}
                                    className={`aspect-square rounded-xl md:rounded-2xl border-2 font-black text-xs md:sm flex items-center justify-center transition-all active:scale-90 ${active ? `${tokens.primaryBtn} text-white shadow-lg` : answered ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-slate-400'}`}
                                  >
                                      {idx + 1}
                                  </button>
                              );
                          })}
                      </div>
                  </div>

                  <div className="p-8 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col md:flex-row justify-between items-center gap-6 shrink-0">
                      {!isReviewing && !isSubmitted && (
                          <button 
                              onClick={handleSaveAndExit}
                              disabled={isSaving}
                              className={`w-full md:w-auto px-12 py-5 ${tokens.primaryBtn} text-white rounded-2xl font-black text-xs tracking-widest shadow-2xl transition-all flex items-center justify-center gap-3 border-b-4 border-black/20`}
                          >
                              {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                              Save and Quit
                          </button>
                      )}
                      <button onClick={() => setStatusMap(false)} className="text-[10px] font-black text-slate-400 tracking-widest hover:text-slate-900 dark:hover:text-white transition-colors">Go back to Question</button>
                  </div>
              </div>
          </div>
      )}

      {/* Sticky Active Exam Header Bar with Circular Progress Rings */}
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-1.5 sm:p-2 md:p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl flex flex-wrap sm:flex-nowrap justify-between items-center sticky top-1 z-50 mb-3 md:mb-6 transition-all gap-1.5 sm:gap-2">
        <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-1 min-w-0 flex-wrap sm:flex-nowrap">
          <button onClick={() => setSession(null)} title="Quit Test" className="flex items-center justify-center p-2 sm:p-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl transition-all active:scale-90 shrink-0 min-h-[36px] min-w-[36px] sm:min-h-[40px] sm:min-w-[40px]">
            <X size={16} strokeWidth={3} />
          </button>

          {/* CIRCULAR PROGRESS RING: QUESTIONS REMAINING */}
          <CircularProgressRing
            progress={questionsProgressPercent}
            size={34}
            strokeWidth={3.5}
            accentColorClass={tokens.accentText}
            label={`${remainingQuestions}`}
            sublabel={
              <>
                <span className="hidden md:inline">Remaining Questions ({currentIndex + 1}/{totalQuestions})</span>
                <span className="hidden sm:inline md:hidden">Remaining ({currentIndex + 1}/{totalQuestions})</span>
                <span className="sm:hidden">Remaining</span>
              </>
            }
          />

          {/* CIRCULAR PROGRESS RING: TIME REMAINING */}
          {!isQuizMode && (
            <CircularProgressRing
              progress={timeRemainingPercent}
              size={34}
              strokeWidth={3.5}
              accentColorClass={tokens.accentText}
              isWarning={timeRemaining < 300}
              label={`${Math.floor(timeRemaining / 60)}:${(timeRemaining % 60).toString().padStart(2, '0')}`}
              sublabel={
                <>
                  <span className="hidden sm:inline">Time Left</span>
                  <span className="sm:hidden">Timer</span>
                </>
              }
            />
          )}
        </div>
        
        <div className="flex items-center gap-2 shrink-0 ml-1">
            {!isReviewing && !isSubmitted && (
                <button 
                    onClick={handleSaveAndExit}
                    disabled={isSaving}
                    className="hidden sm:flex items-center gap-2 px-3 md:px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-black text-[9px] tracking-widest transition-all"
                >
                    {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save and Quit
                </button>
            )}
            <button 
                onClick={handleDownloadExamPdf} 
                disabled={isGeneratingPdf}
                title="Download Exam PDF" 
                className="p-2.5 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900 rounded-xl transition-all shadow-sm min-h-[40px] min-w-[40px] flex items-center justify-center disabled:opacity-50"
            >
                {isGeneratingPdf ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            </button>
            <button 
                onClick={() => window.print()} 
                title="Print Exam Packet via Browser" 
                className="p-2.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl hover:text-emerald-600 transition-all shadow-sm min-h-[40px] min-w-[40px] flex items-center justify-center"
            >
                <Printer size={16} />
            </button>
            <button onClick={() => setStatusMap(true)} className={`p-2.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl hover:${tokens.accentText} transition-all shadow-sm min-h-[40px] min-w-[40px] flex items-center justify-center`}>
                <Menu size={16} />
            </button>
        </div>
      </div>

      <div key={currentIndex} className="bg-white dark:bg-slate-900 rounded-2xl md:rounded-[3rem] p-4 sm:p-6 md:p-12 shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col animate-slide-up relative min-h-0 md:min-h-[550px]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-8">
              <span className={`w-fit px-2.5 py-1 text-[8px] md:text-[9px] font-black rounded-lg border tracking-widest uppercase ${isOrdering ? 'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900' : 'bg-medical-50 text-medical-600 border-medical-100 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900'}`}>
                {isOrdering ? <><ListOrdered size={11} className="inline mr-1.5 mb-0.5 align-middle"/> Put in Order</> : currentQ.category}
              </span>

              {!isReviewing && !isSubmitted && userAnswers[currentQ._id] && (
                  <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700 animate-pop self-start sm:self-auto">
                      <span className="text-[8px] sm:text-[9px] font-black text-slate-400 px-2 uppercase tracking-wider">How sure are you?</span>
                      <button 
                        onClick={() => setConfidence('low')} 
                        className={`p-2 rounded-lg transition-all active:scale-95 flex items-center justify-center min-h-[36px] min-w-[36px] ${currentConfidence === 'low' ? 'bg-amber-500 text-white shadow-md' : 'text-slate-450 hover:text-slate-650'}`}
                        title="Not very sure"
                      >
                          <Frown size={16} />
                      </button>
                      <button 
                        onClick={() => setConfidence('high')} 
                        className={`p-2 rounded-lg transition-all active:scale-95 flex items-center justify-center min-h-[36px] min-w-[36px] ${currentConfidence === 'high' ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-450 hover:text-slate-650'}`}
                        title="Very sure"
                      >
                          <Smile size={16} />
                      </button>
                  </div>
              )}
          </div>
          
          <div className="mb-4 md:mb-10 flex items-start justify-between gap-4">
            <h3 className="text-xl md:text-3xl font-black text-slate-900 dark:text-white leading-snug tracking-tight flex-1">"{currentQ.text}"</h3>
            <button 
              onClick={() => {
                if (currentQ) {
                  const optionsText = currentQ.options 
                    ? currentQ.options.map((o, idx2) => 
                        isOrdering ? `Step ${idx2 + 1}: ${o.text}` : `Choice ${String.fromCharCode(65 + idx2)}: ${o.text}`
                      ).join('. ') 
                    : '';
                  const rationaleSpoken = (isReviewing && currentQ.rationale) ? `. Explanation rationale: ${currentQ.rationale}` : '';
                  handleSpeak(`${currentQ.text}. ${optionsText}${rationaleSpoken}`);
                }
              }} 
              className={`p-3.5 sm:p-3 rounded-xl transition-all shrink-0 active:scale-95 border min-h-[44px] min-w-[44px] flex items-center justify-center ${
                isSpeaking 
                  ? 'bg-rose-50 text-rose-500 animate-pulse border-rose-200 dark:bg-rose-950/30 dark:border-rose-800' 
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-indigo-400 border-transparent'
              }`}
              title={isSpeaking ? "Stop Speaking" : "Auditory Reader (TTS)"}
              aria-label={isSpeaking ? "Stop reading question" : "Read question and choices aloud"}
            >
              {isSpeaking ? <VolumeX size={20} className="text-rose-500" /> : <Volume2 size={20} />}
            </button>
          </div>
          
          <div className="space-y-3.5 mt-2">
              {(currentQ.options || []).map((opt, idx) => {
                  const val = opt._id || idx.toString();
                  const sequenceIdx = isOrdering ? (isReviewing ? userAns?.split(',').indexOf(val) : currentSequence.indexOf(val)) : -1;
                  const isSelected = isOrdering ? sequenceIdx !== -1 : (userAns === val);
                  
                  let style = isSelected 
                    ? `${tokens.accentBorder} ${tokens.badgeBg} ${tokens.accentText} shadow-md ring-2 ring-emerald-500/10 scale-[1.01]` 
                    : `border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/90 hover:bg-indigo-50 dark:hover:bg-indigo-950/80 hover:border-indigo-600 dark:hover:border-indigo-400 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] text-slate-900 dark:text-slate-100 font-bold`;
                  
                  if (isReviewing || (isQuizMode && userAns)) {
                      if (isOrdering) {
                        const correctSequence = currentQ.options.map((o, oIdx) => o._id || oIdx.toString());
                        const isAtCorrectPos = correctSequence[sequenceIdx] === val;
                        if (isAtCorrectPos) style = "border-emerald-500 dark:border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/35 text-emerald-955 dark:text-emerald-250 ring-2 ring-emerald-500/10";
                        else if (isSelected) style = "border-rose-500 dark:border-rose-500/40 bg-rose-50/60 dark:bg-rose-955/35 text-rose-955 dark:text-rose-250 ring-2 ring-rose-500/10";
                        else style = "opacity-35 grayscale border-transparent bg-slate-100/40 dark:bg-slate-950";
                      } else {
                        if (opt.isCorrect) style = `border-emerald-600 dark:border-emerald-505 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200 shadow-lg scale-[1.015] z-10 ring-4 ring-emerald-500/15`;
                        else if (isSelected) style = 'border-rose-600 dark:border-rose-550 bg-rose-50 dark:bg-rose-955/40 text-rose-900 dark:text-rose-200 shadow-inner opacity-80 ring-2 ring-rose-500/10';
                        else style = 'opacity-30 grayscale border-transparent bg-slate-50 dark:bg-slate-955 scale-95';
                      }
                  }
                  
                  return (
                    <button 
                        key={val} 
                        onClick={() => handleAnswer(currentQ._id, val)} 
                        disabled={isReviewing || (isOrdering && isSelected)} 
                        className={`w-full p-4 md:p-5 rounded-2xl border-2 text-left font-semibold flex items-center gap-4 transition-all duration-300 relative group min-h-[64px] ${style}`}
                    >
                        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl border-2 flex items-center justify-center shrink-0 font-black text-[10px] md:text-xs transition-colors ${isSelected ? `${tokens.primaryBtn} text-white shadow-inner` : `bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 group-hover:border-indigo-600 dark:group-hover:border-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900 group-hover:scale-105`}`}>
                            {isOrdering && isSelected ? (sequenceIdx + 1) : String.fromCharCode(65 + idx)}
                        </div>
                        <span className="text-sm md:text-base tracking-tight leading-snug pr-12 md:pr-14 flex-1">{opt.text}</span>
                        {(isReviewing || (isQuizMode && userAns)) && !isOrdering && opt.isCorrect && <CheckCircle2 className="absolute right-4 md:right-6 text-emerald-500 animate-pop shrink-0" size={20} />}
                    </button>
                  );
              })}
          </div>

          {(isReviewing || (isQuizMode && userAns)) && (
            <div className="space-y-6 mt-6 md:mt-12">
                <div className="p-4 md:p-8 bg-indigo-50/50 rounded-2xl md:rounded-[2.5rem] border-2 border-indigo-100 animate-slide-up relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5"><Brain size={60} className="text-indigo-900" /></div>
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-[8px] md:text-[10px] font-black text-indigo-500 tracking-widest flex items-center gap-1.5"><Sparkles size={12}/> Explanation</h4>
                        <button 
                            onClick={handleDeepDive}
                            disabled={isDeepDiving}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl font-black text-[9px] tracking-widest shadow-xl active:scale-95 disabled:opacity-50"
                        >
                            {isDeepDiving ? <Loader2 size={12} className="animate-spin"/> : <SearchCheck size={12}/>}
                            See Details
                        </button>
                    </div>
                    <p className="text-xs md:text-base font-bold text-indigo-900 border-l-4 border-indigo-400 pl-3 md:pl-6 leading-relaxed">"{currentQ.explanation}"</p>
                </div>

                {deepRationale && (
                    <div className="bg-slate-900 rounded-[2rem] p-8 border border-white/10 animate-pop space-y-8">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-500 text-white rounded-xl shadow-lg"><Binary size={18}/></div>
                            <div>
                                <h5 className="text-white font-black text-sm tracking-tight">Why other answers are wrong</h5>
                                <p className="text-[8px] font-black text-slate-500 tracking-widest">Helper tips</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {deepRationale.distractorAnalyses.map((item: any, i: number) => (
                                <div key={i} className="p-5 bg-white/5 border border-white/5 rounded-2xl space-y-2 group hover:border-indigo-500/30 transition-all">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                                        <p className="text-[9px] font-black text-slate-400 tracking-tighter truncate leading-none">{item.optionText}</p>
                                    </div>
                                    <p className="text-xs text-slate-300 font-medium leading-relaxed">"{item.whyIncorrect}"</p>
                                </div>
                            ))}
                        </div>
                        
                        <div className="pt-4 border-t border-white/5 flex items-center gap-3 opacity-60">
                            <Info size={14} className="text-indigo-400" />
                            <p className="text-[8px] font-black text-slate-400 tracking-widest">Answers double-checked by our nursing tutors.</p>
                        </div>
                    </div>
                )}
            </div>
          )}
      </div>

      {/* Keyboard layout visual indicators */}
      <div className="flex justify-center items-center gap-4 text-[9px] font-black text-slate-455 dark:text-slate-500 tracking-widest uppercase px-1 mt-4">
          <span className="flex items-center gap-1"><Keyboard size={12}/> Keys you can use:</span>
          {!userAns ? (
              <>
                  <span>[A-D] / [1-4] to choose answer</span>
              </>
          ) : (
              <>
                  <span>[Space] / [Right Arrow] / [Enter] for Next Question</span>
              </>
          )}
      </div>

      <div className="w-full">
        <AdUnit placement={isQuizMode ? 'practice_quiz_bottom' : 'practice_exam_bottom'} refreshKey={adRefreshKey} />
      </div>

      <div className="sticky bottom-2 z-[100] pb-safe mt-6">
          <div className="max-w-2xl mx-auto p-2 pointer-events-none">
              <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 p-2 md:p-3 rounded-2xl md:rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex justify-between items-center gap-2.5 md:gap-4 pointer-events-auto">
                  <div className="flex gap-2">
                    <button 
                      onClick={handleBack} 
                      disabled={currentIndex === 0} 
                      title="Previous Question" 
                      className="w-12 h-12 md:w-14 md:h-14 bg-white/5 text-slate-400 hover:text-white rounded-xl md:rounded-2xl transition-all flex items-center justify-center disabled:opacity-0 active:scale-90 shrink-0"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button 
                      onClick={() => setStatusMap(true)} 
                      title="Test Progress" 
                      className="w-12 h-12 md:w-14 md:h-14 bg-white/5 text-indigo-400 rounded-xl md:rounded-2xl transition-all flex items-center justify-center active:scale-90 border border-indigo-500/20 shrink-0"
                    >
                      <Menu size={20}/>
                    </button>
                  </div>
                  <button 
                    onClick={handleNext} 
                    disabled={!isReviewing && !userAns && (isOrdering ? currentSequence.length < (currentQ?.options?.length || 0) : true)}
                    className={`flex-1 h-12 md:h-14 rounded-xl md:rounded-2xl font-black text-xs md:sm tracking-widest shadow-2xl transition-all active:scale-95 flex items-center gap-2 md:gap-3 justify-center ${currentIndex === (session?.questions?.length || 1) - 1 ? (isReviewing ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-emerald-600 hover:bg-emerald-500') : tokens.primaryBtn} text-white border-b-4 border-black/20 disabled:opacity-30`}
                  >
                    {currentIndex === (session?.questions?.length || 1) - 1 ? (isReviewing ? 'Exit' : 'Finish Test') : 'Next'} 
                    {currentIndex === (session?.questions?.length || 1) - 1 ? <CheckCircle size={18} /> : <ArrowRight size={18} />}
                  </button>
              </div>
          </div>
      </div>
    </div>
    <PrintExamDocument 
      examTitle={session?.examConfig?.vendor ? `${session.examConfig.vendor} Practice Exam` : "National Board Practice Exam"}
      certName={activeCert?.name || "Nurse Aide Certification (CNA)"}
      vendor={session?.examConfig?.vendor || "Registry Curated"}
      questions={session?.questions || []}
      userAnswers={userAnswers}
      isSubmitted={isReviewing}
      score={activeAnalytics?.percentScore || 0}
      passed={activeAnalytics?.passed || false}
      timeSpentMinutes={Math.round((Object.values(timeSpent).reduce((a, b) => a + b, 0) || 0) / 60)}
      categoryBreakdown={activeAnalytics?.categoryBreakdown || []}
    />
    <AiFeatureAuthModal 
      isOpen={showAiAuthModal}
      onClose={() => setShowAiAuthModal(false)}
      featureTitle="Faculty Clinical Exam Rationale"
      featureDescription="Create a free account or sign in to access instant educator-curated distractor analyses and board-aligned clinical rationales."
      source="exam_simulator_deep_dive"
    />
    <AiFeatureAuthModal 
      isOpen={showGuestExamAuthModal}
      onClose={() => setShowGuestExamAuthModal(false)}
      featureTitle={
        effectiveGuestExamLimit <= 0 
          ? "Account Required for Practice Exams" 
          : guestExamsCompleted >= effectiveGuestExamLimit 
          ? "Guest Practice Exam Limit Reached"
          : lockedSetTarget 
          ? `Unlock Practice Exam Set ${lockedSetTarget.toString().padStart(2, '0')}` 
          : "Unlock All Practice Exam Sets"
      }
      featureDescription={
        guestExamsCompleted >= effectiveGuestExamLimit
          ? `You have completed your free guest practice test allowance (${guestExamsCompleted} of ${effectiveGuestExamLimit} tests taken). Create your free student account to unlock all state-aligned exam sets with unlimited practice attempts!`
          : "Create your free student account or sign in to unlock all full-length state-aligned practice exam sets, save your exam scores, and build your Mistake Vault."
      }
      source={`exam_simulator_locked_set_${lockedSetTarget || 'all'}`}
      badgeLabel={guestExamsCompleted >= effectiveGuestExamLimit ? "Practice Exam Limit Reached" : "Free Student Account Required"}
      benefits={[
        "Access all practice exam sets across every state and board vendor",
        "Save all test attempts, detailed question breakdowns, and score trajectories",
        "Auto-collect missed questions into your personal Mistake Vault",
        "100% Free forever with no credit card required"
      ]}
      dismissText={
        guestExamsCompleted >= effectiveGuestExamLimit || effectiveGuestExamLimit <= 0 
          ? "Close" 
          : effectiveGuestExamLimit === 1 
          ? "Continue with Free Exam Set 01" 
          : `Continue with Free Exam Sets (01-${effectiveGuestExamLimit.toString().padStart(2, '0')})`
      }
    />
  </>
  );
};
